import { DiscordSDK, Events, RPCCloseCodes } from '@discord/embedded-app-sdk';
import * as THREE from 'three';
import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader.js';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { MeshoptDecoder } from 'three/examples/jsm/libs/meshopt_decoder.module.js';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { RoomEnvironment } from 'three/examples/jsm/environments/RoomEnvironment.js';
import { Line2 } from 'three/examples/jsm/lines/Line2.js';
import { LineGeometry } from 'three/examples/jsm/lines/LineGeometry.js';
import { LineMaterial } from 'three/examples/jsm/lines/LineMaterial.js';
import {
  cameraRefreshDelay,
  cameraRequestHeaders,
  isCameraAbort,
  newCameraViewerId,
} from './camera_polling.mjs';
import './style.css';

const elements = {
  canvas: document.querySelector('#model-canvas'),
  viewer: document.querySelector('#viewer'),
  objectName: document.querySelector('#object-name'),
  connection: document.querySelector('#connection-state'),
  loading: document.querySelector('#loading-panel'),
  loadingTitle: document.querySelector('#loading-title'),
  loadingDetail: document.querySelector('#loading-detail'),
  progress: document.querySelector('#loading-progress'),
  error: document.querySelector('#error-panel'),
  errorTitle: document.querySelector('#error-title'),
  errorDetail: document.querySelector('#error-detail'),
  retry: document.querySelector('#retry-button'),
  reset: document.querySelector('#reset-button'),
  rotate: document.querySelector('#rotate-button'),
  mapControls: document.querySelector('#map-controls'),
  icons: document.querySelector('#icons-button'),
  labels: document.querySelector('#labels-button'),
  roads: document.querySelector('#roads-button'),
  rail: document.querySelector('#rail-button'),
  rivers: document.querySelector('#rivers-button'),
  models: document.querySelector('#models-button'),
  team: document.querySelector('#team-button'),
  teamFilterButton: document.querySelector('#team-filter-button'),
  teamFilterPanel: document.querySelector('#team-filter-panel'),
  teamFilterClose: document.querySelector('#team-filter-close'),
  teamFilterList: document.querySelector('#team-filter-list'),
  teamFilterSummary: document.querySelector('#team-filter-summary'),
  teamFilterAll: document.querySelector('#team-filter-all'),
  teamFilterNone: document.querySelector('#team-filter-none'),
  deaths: document.querySelector('#deaths-button'),
  deathFilterButton: document.querySelector('#death-filter-button'),
  deathFilterPanel: document.querySelector('#death-filter-panel'),
  deathFilterClose: document.querySelector('#death-filter-close'),
  deathFilterList: document.querySelector('#death-filter-list'),
  deathFilterSummary: document.querySelector('#death-filter-summary'),
  deathFilterAll: document.querySelector('#death-filter-all'),
  deathFilterNone: document.querySelector('#death-filter-none'),
  trails: document.querySelector('#trails-button'),
  trailFilterButton: document.querySelector('#trail-filter-button'),
  trailFilterPanel: document.querySelector('#trail-filter-panel'),
  trailFilterClose: document.querySelector('#trail-filter-close'),
  trailFilterList: document.querySelector('#trail-filter-list'),
  trailFilterSummary: document.querySelector('#trail-filter-summary'),
  trailFilterAll: document.querySelector('#trail-filter-all'),
  trailFilterNone: document.querySelector('#trail-filter-none'),
  trailFadeToggle: document.querySelector('#trail-fade-toggle'),
  trailRangePicker: document.querySelector('#trail-range-picker'),
  trailRangeSelect: document.querySelector('#trail-range-select'),
  cameras: document.querySelector('#cameras-button'),
  turrets: document.querySelector('#turrets-button'),
  securityFacing: document.querySelector('#security-facing-button'),
  teamIntelPanel: document.querySelector('#team-intel-panel'),
  teamIntelSummary: document.querySelector('#team-intel-summary'),
  teamIntelDetail: document.querySelector('#team-intel-detail'),
  participantsPanel: document.querySelector('#participants-panel'),
  participantsSummary: document.querySelector('#participants-summary'),
  participantsDetail: document.querySelector('#participants-detail'),
  monumentFilterButton: document.querySelector('#monument-filter-button'),
  monumentFilterPanel: document.querySelector('#monument-filter-panel'),
  monumentFilterClose: document.querySelector('#monument-filter-close'),
  monumentFilterList: document.querySelector('#monument-filter-list'),
  monumentFilterSummary: document.querySelector('#monument-filter-summary'),
  monumentFilterAll: document.querySelector('#monument-filter-all'),
  monumentFilterNone: document.querySelector('#monument-filter-none'),
  overlayPicker: document.querySelector('#overlay-picker'),
  overlaySelect: document.querySelector('#overlay-select'),
  overlay: document.querySelector('#overlay-button'),
  overlayInfo: document.querySelector('#overlay-info'),
  overlayInfoGroup: document.querySelector('#overlay-info-group'),
  overlayInfoTitle: document.querySelector('#overlay-info-title'),
  overlayInfoEvidence: document.querySelector('#overlay-info-evidence'),
  overlayInfoDescription: document.querySelector('#overlay-info-description'),
  overlayInfoGradient: document.querySelector('#overlay-info-gradient'),
  grid: document.querySelector('#grid-button'),
  viewMode: document.querySelector('#view-mode-button'),
  recenter: document.querySelector('#recenter-button'),
  selectionPanel: document.querySelector('#selection-panel'),
  selectionKind: document.querySelector('#selection-kind'),
  selectionName: document.querySelector('#selection-name'),
  selectionDetail: document.querySelector('#selection-detail'),
  selectionTeamButton: document.querySelector('#selection-team-button'),
  selectionTrailButton: document.querySelector('#selection-trail-button'),
  selectionSecurityViewButton: document.querySelector('#selection-security-view-button'),
  securityViewerPanel: document.querySelector('#security-viewer-panel'),
  securityViewerKind: document.querySelector('#security-viewer-kind'),
  securityViewerName: document.querySelector('#security-viewer-name'),
  securityViewerClose: document.querySelector('#security-viewer-close'),
  securityViewerImage: document.querySelector('#security-viewer-image'),
  securityViewerLoading: document.querySelector('#security-viewer-loading'),
  securityViewerStatus: document.querySelector('#security-viewer-status'),
  toolbar: document.querySelector('#viewer-toolbar'),
  camsDashboard: document.querySelector('#cams-dashboard'),
  camsRefresh: document.querySelector('#cams-refresh-button'),
  camsDeviceSummary: document.querySelector('#cams-device-summary'),
  camsDeviceList: document.querySelector('#cams-device-list'),
  camsActiveKind: document.querySelector('#cams-active-kind'),
  camsActiveName: document.querySelector('#cams-active-name'),
  camsQualityPill: document.querySelector('#cams-quality-pill'),
  camsLiveImage: document.querySelector('#cams-live-image'),
  camsLiveLoading: document.querySelector('#cams-live-loading'),
  camsLiveStatus: document.querySelector('#cams-live-status'),
  camsStop: document.querySelector('#cams-stop-button'),
  diagnostics: document.querySelector('#diagnostics'),
  diagnosticsList: document.querySelector('#diagnostics-list'),
};

// Discord's proxy now accepts normal same-origin paths. The older /.proxy
// prefix remains compatible, but is no longer required for new Activities.
const apiUrl = (path) => path;
const NEKO_ACTIVITY_AUTH_KEY = 'meg_neko_activity_auth_v1';
const params = new URLSearchParams(window.location.search);
const debugRequested = params.get('debug') === '1';
let retryAction = null;
let viewerRuntime = null;
let discordSdk = null;
let discordReady = null;
let discordAuthentication = null;
let activityClosed = false;
let participantTrackingActive = false;
let participantUpdateHandler = null;
const activityAbort = new AbortController();
const cameraViewerId = newCameraViewerId();
const activityLifecycle = {
  phase: 'starting',
  closeReason: null,
  renderedFrames: 0,
  rendererStopped: true,
  rendererPaused: false,
};

window.__MEG_3D_LIFECYCLE__ = () => ({ ...activityLifecycle });

function redactedDiagnostic(error) {
  return {
    name: String(error?.name || 'Error').slice(0, 80),
    message: String(error?.message || 'Viewer operation failed')
      .replace(/\/api\/3d\/model\/[A-Za-z0-9_-]{32}/g, '/api/3d/model/<redacted>')
      .replace(/\/api\/3d\/world\/[A-Za-z0-9_-]{32}/g, '/api/3d/world/<redacted>')
      .replace(/([?&]dev_token=)[A-Za-z0-9_-]{32}/g, '$1<redacted>')
      .slice(0, 500),
  };
}

function setLoading(title, detail, percent = 0) {
  elements.loadingTitle.textContent = title;
  elements.loadingDetail.textContent = detail;
  elements.progress.value = Math.max(0, Math.min(100, percent));
  elements.loading.classList.remove('hidden');
  elements.error.hidden = true;
}

function setConnected(label = 'Ready') {
  elements.connection.textContent = label;
  elements.connection.classList.add('ready');
}

function showError(title, detail, retry = null) {
  elements.loading.classList.add('hidden');
  elements.errorTitle.textContent = title;
  elements.errorDetail.textContent = detail;
  elements.error.hidden = false;
  elements.retry.hidden = retry == null;
  retryAction = retry;
  elements.connection.textContent = 'Unavailable';
  elements.connection.classList.remove('ready');
}

async function requestJson(path, options = {}) {
  const response = await fetch(apiUrl(path), {
    ...options,
    signal: options.signal || activityAbort.signal,
    headers: {
      Accept: 'application/json',
      ...(options.body ? { 'Content-Type': 'application/json' } : {}),
      ...(options.headers || {}),
    },
  });
  let payload = {};
  try {
    payload = await response.json();
  } catch {
    payload = {};
  }
  if (!response.ok) {
    const error = new Error(payload.message || 'MEG’s Activity request failed.');
    error.code = payload.error || `http_${response.status}`;
    throw error;
  }
  return payload;
}

async function openActivitySession() {
  try {
    const config = await requestJson('/api/3d/config');
    if (!config.application_id) {
      throw new Error('The Discord Activity application ID is not configured.');
    }

    const devToken = params.get('dev_token');
    if (config.dev_mode && devToken) {
      setLoading('Opening local viewer…', 'Resolving the verified development model', 8);
      return {
        config,
        session: await requestJson('/api/3d/dev/session', {
          method: 'POST',
          body: JSON.stringify({ selection_token: devToken }),
        }),
        instanceId: 'local-development',
      };
    }

    setLoading('Connecting to Discord…', 'Starting the Embedded App SDK', 4);
    if (!discordSdk) {
      discordSdk = new DiscordSDK(config.application_id);
      discordReady = discordSdk.ready();
    }
    const instanceId = discordSdk.instanceId;
    if (!instanceId) {
      throw new Error('Discord did not provide an Activity instance. Open this Activity from MEG.');
    }
    try {
      await discordReady;
    } catch (error) {
      // A failed initial handshake can be attempted again. Once READY succeeds,
      // keep this SDK for the full iframe lifetime as Discord Activities are SPAs.
      discordSdk = null;
      discordReady = null;
      throw error;
    }

    if (discordAuthentication?.expiresAt <= Date.now() + 5_000) {
      discordAuthentication = null;
    }
    if (!discordAuthentication) {
      setLoading('Authorizing viewer…', 'Confirming your Discord identity', 9);
      const { code } = await discordSdk.commands.authorize({
        client_id: config.application_id,
        response_type: 'code',
        state: crypto.randomUUID(),
        prompt: 'none',
        scope: ['identify'],
      });
      const token = await requestJson('/api/3d/oauth/token', {
        method: 'POST',
        body: JSON.stringify({ code }),
      });
      const authenticated = await discordSdk.commands.authenticate({
        access_token: token.access_token,
      });
      if (!authenticated?.user?.id) {
        throw new Error('Discord authentication did not complete.');
      }
      discordAuthentication = {
        accessToken: token.access_token,
        userId: authenticated.user.id,
        expiresAt: Date.now() + Math.max(60, Number(token.expires_in) || 300) * 1_000,
      };
    }

    setLoading('Finding your Activity…', 'Matching this session to the exact MEG launch', 14);
    const session = await requestJson('/api/3d/session', {
      method: 'POST',
      headers: { Authorization: `Bearer ${discordAuthentication.accessToken}` },
      body: JSON.stringify({ instance_id: instanceId }),
    });
    return { config, session, instanceId };
  } catch (error) {
    if (error.code) throw error;
    const safe = new Error('Discord could not authenticate this Activity. Close it and launch it from MEG again.');
    safe.code = 'activity_auth_failed';
    throw safe;
  }
}

function storeNekoActivityAuthentication(instanceId) {
  if (
    !discordAuthentication?.accessToken
    || !discordAuthentication?.userId
    || discordAuthentication.expiresAt <= Date.now()
  ) {
    throw new Error('Discord watch-room authentication is unavailable.');
  }
  sessionStorage.setItem(NEKO_ACTIVITY_AUTH_KEY, JSON.stringify({
    version: 1,
    instanceId,
    accessToken: discordAuthentication.accessToken,
    userId: discordAuthentication.userId,
    expiresAt: discordAuthentication.expiresAt,
  }));
}

function participantDisplayName(participant) {
  const globalName = String(participant?.global_name || '').trim();
  if (globalName) return globalName.slice(0, 40);
  const username = String(participant?.username || '').trim();
  if (username) return username.slice(0, 40);
  return 'Discord user';
}

function updateParticipantsPanel(payload) {
  const participants = Array.isArray(payload?.participants) ? payload.participants : [];
  if (!elements.participantsPanel || !elements.participantsSummary || !elements.participantsDetail) return;
  if (!participants.length) {
    elements.participantsPanel.hidden = true;
    return;
  }

  const names = participants.map(participantDisplayName);
  elements.participantsSummary.textContent = `${participants.length} viewing`;
  const visibleNames = names.slice(0, 5);
  elements.participantsDetail.textContent = visibleNames.join(' · ') + (names.length > visibleNames.length ? ` · +${names.length - visibleNames.length}` : '');
  elements.participantsPanel.hidden = false;
}

async function startParticipantTracking() {
  if (!discordSdk || activityClosed || participantTrackingActive) return;
  try {
    const current = await discordSdk.commands.getInstanceConnectedParticipants();
    updateParticipantsPanel(current);
    participantUpdateHandler = (payload) => updateParticipantsPanel(payload);
    discordSdk.subscribe(Events.ACTIVITY_INSTANCE_PARTICIPANTS_UPDATE, participantUpdateHandler);
    participantTrackingActive = true;
  } catch (error) {
    // Participant presence is convenience UI only. Never block the map viewer if
    // Discord cannot provide it on a particular client/platform.
    console.warn('Discord Activity participant list unavailable', redactedDiagnostic(error));
    elements.participantsPanel.hidden = true;
  }
}

function stopParticipantTracking() {
  if (discordSdk && participantTrackingActive && participantUpdateHandler) {
    try {
      discordSdk.unsubscribe(Events.ACTIVITY_INSTANCE_PARTICIPANTS_UPDATE, participantUpdateHandler);
    } catch {
      // Discord may already have closed the RPC transport.
    }
  }
  participantTrackingActive = false;
  participantUpdateHandler = null;
}

function disposeSceneResources(scene) {
  const geometries = new Set();
  const materials = new Set();
  const textures = new Set();
  const imageBitmaps = new Set();
  const skeletons = new Set();

  scene.traverse((object) => {
    if (object.geometry?.dispose) geometries.add(object.geometry);
    const objectMaterials = Array.isArray(object.material) ? object.material : [object.material];
    for (const material of objectMaterials) {
      if (!material?.dispose) continue;
      materials.add(material);
      for (const value of Object.values(material)) {
        if (value?.isTexture && value.dispose) {
          textures.add(value);
          const image = value.source?.data || value.image;
          if (image?.close) imageBitmaps.add(image);
        }
      }
      for (const uniform of Object.values(material.uniforms || {})) {
        const values = Array.isArray(uniform?.value) ? uniform.value : [uniform?.value];
        for (const value of values) {
          if (!value?.isTexture || !value.dispose) continue;
          textures.add(value);
          const image = value.source?.data || value.image;
          if (image?.close) imageBitmaps.add(image);
        }
      }
    }
    if (object.skeleton?.dispose) skeletons.add(object.skeleton);
    if (object.shadow?.map?.dispose) object.shadow.map.dispose();
    if (object.shadow?.mapPass?.dispose) object.shadow.mapPass.dispose();
  });

  for (const texture of textures) texture.dispose();
  for (const image of imageBitmaps) image.close();
  for (const skeleton of skeletons) skeleton.dispose();
  for (const material of materials) material.dispose();
  for (const geometry of geometries) geometry.dispose();
  scene.clear();
}

function createViewer() {
  const renderer = new THREE.WebGLRenderer({
    canvas: elements.canvas,
    alpha: true,
    antialias: true,
    powerPreference: 'default',
  });
  renderer.outputColorSpace = THREE.SRGBColorSpace;
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  renderer.toneMappingExposure = 0.86;
  renderer.shadowMap.enabled = true;
  renderer.shadowMap.type = THREE.PCFSoftShadowMap;
  renderer.shadowMap.autoUpdate = false;
  renderer.setClearColor(0x000000, 0);

  const scene = new THREE.Scene();
  const camera = new THREE.PerspectiveCamera(31, 1, 0.01, 1000);
  const controls = new OrbitControls(camera, renderer.domElement);
  controls.enableDamping = true;
  controls.dampingFactor = 0.07;
  controls.enablePan = true;
  controls.screenSpacePanning = true;
  controls.autoRotate = false;
  controls.autoRotateSpeed = 0.8;
  controls.zoomToCursor = true;
  controls.mouseButtons.LEFT = THREE.MOUSE.ROTATE;
  controls.mouseButtons.MIDDLE = THREE.MOUSE.DOLLY;
  controls.mouseButtons.RIGHT = THREE.MOUSE.PAN;
  controls.touches.ONE = THREE.TOUCH.ROTATE;
  controls.touches.TWO = THREE.TOUCH.DOLLY_PAN;

  const environmentScene = new RoomEnvironment();
  const pmrem = new THREE.PMREMGenerator(renderer);
  const environmentTarget = pmrem.fromScene(environmentScene, 0.04);
  scene.environment = environmentTarget.texture;
  environmentScene.dispose();
  pmrem.dispose();

  scene.add(new THREE.HemisphereLight(0xf4fbff, 0x3d4547, 1.7));
  const key = new THREE.DirectionalLight(0xffffff, 3.0);
  key.position.set(4, 7, 5);
  key.castShadow = true;
  key.shadow.mapSize.set(1024, 1024);
  key.shadow.bias = -0.0004;
  scene.add(key);
  const fill = new THREE.DirectionalLight(0xb8d9ff, 1.2);
  fill.position.set(-5, 2, 3);
  scene.add(fill);
  const rim = new THREE.DirectionalLight(0xffd1a7, 0.95);
  rim.position.set(2, 4, -5);
  scene.add(rim);

  const groundMaterial = new THREE.ShadowMaterial({ color: 0x12232a, opacity: 0.22 });
  const ground = new THREE.Mesh(new THREE.PlaneGeometry(20, 20), groundMaterial);
  ground.rotation.x = -Math.PI / 2;
  ground.receiveShadow = true;
  ground.visible = false;
  scene.add(ground);

  let stopped = false;
  let paused = document.hidden;
  let animationFrame = null;
  let renderRequested = true;
  let settlingFrames = 2;
  let lastFrameTime = 0;
  let previousRenderTime = 0;
  const recentFrameTimes = [];
  const cleanupCallbacks = [];
  const minimumFrameInterval = 1000 / 30;

  const scheduleFrame = () => {
    if (stopped || paused || animationFrame !== null) return;
    animationFrame = requestAnimationFrame(animate);
  };

  const invalidate = (settleFor = 2) => {
    if (stopped) return;
    renderRequested = true;
    settlingFrames = Math.max(settlingFrames, settleFor);
    scheduleFrame();
  };

  const onControlsChange = () => invalidate(6);
  controls.addEventListener('change', onControlsChange);

  const resize = () => {
    const { width, height } = elements.viewer.getBoundingClientRect();
    const safeWidth = Math.max(1, Math.floor(width));
    const safeHeight = Math.max(1, Math.floor(height));
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 1.5));
    renderer.setSize(safeWidth, safeHeight, false);
    camera.aspect = safeWidth / safeHeight;
    camera.updateProjectionMatrix();
    invalidate(2);
  };
  const observer = new ResizeObserver(resize);
  observer.observe(elements.viewer);
  resize();

  function animate(time) {
    animationFrame = null;
    if (stopped || paused) return;
    if (lastFrameTime && time - lastFrameTime < minimumFrameInterval) {
      scheduleFrame();
      return;
    }
    const deltaSeconds = lastFrameTime ? Math.min((time - lastFrameTime) / 1000, 0.1) : 0;
    lastFrameTime = time;
    const controlsChanged = controls.update(deltaSeconds);
    if (renderRequested || controlsChanged || controls.autoRotate) {
      renderer.render(scene, camera);
      if (previousRenderTime) {
        recentFrameTimes.push(time - previousRenderTime);
        if (recentFrameTimes.length > 60) recentFrameTimes.shift();
      }
      previousRenderTime = time;
      activityLifecycle.renderedFrames += 1;
      renderRequested = false;
    }
    settlingFrames = Math.max(0, settlingFrames - 1);
    if (controls.autoRotate || controlsChanged || settlingFrames > 0) scheduleFrame();
  }

  activityLifecycle.rendererStopped = false;
  activityLifecycle.rendererPaused = paused;
  scheduleFrame();

  return {
    renderer,
    scene,
    camera,
    controls,
    ground,
    key,
    resize,
    invalidate,
    addCleanup(callback) {
      cleanupCallbacks.push(callback);
    },
    isStopped() {
      return stopped;
    },
    performanceMetrics() {
      const frameTime = recentFrameTimes.length
        ? recentFrameTimes.reduce((total, value) => total + value, 0) / recentFrameTimes.length
        : 0;
      return {
        fps: frameTime > 0 ? 1000 / frameTime : 0,
        frameTime,
        drawCalls: renderer.info.render.calls,
        triangles: renderer.info.render.triangles,
        geometries: renderer.info.memory.geometries,
        textures: renderer.info.memory.textures,
      };
    },
    pause() {
      if (stopped || paused) return;
      paused = true;
      activityLifecycle.rendererPaused = true;
      if (animationFrame !== null) {
        cancelAnimationFrame(animationFrame);
        animationFrame = null;
      }
    },
    resume() {
      if (stopped || !paused) return;
      paused = false;
      lastFrameTime = 0;
      activityLifecycle.rendererPaused = false;
      invalidate(2);
    },
    stop({ releaseContext = false } = {}) {
      if (stopped) return;
      stopped = true;
      activityLifecycle.rendererStopped = true;
      activityLifecycle.rendererPaused = true;
      if (animationFrame !== null) {
        cancelAnimationFrame(animationFrame);
        animationFrame = null;
      }
      observer.disconnect();
      controls.removeEventListener('change', onControlsChange);
      for (const callback of cleanupCallbacks.splice(0).reverse()) {
        try {
          callback();
        } catch {
          // Cleanup is best effort; remaining GPU resources must still release.
        }
      }
      controls.dispose();
      scene.environment = null;
      environmentTarget.dispose();
      disposeSceneResources(scene);
      renderer.renderLists.dispose();
      renderer.dispose();
      if (releaseContext) renderer.forceContextLoss();
    },
  };
}

function frameModel(runtime, root) {
  root.updateMatrixWorld(true);
  const initialBox = new THREE.Box3().setFromObject(root);
  if (initialBox.isEmpty()) throw new Error('The GLB contains no renderable model bounds.');

  const initialCenter = initialBox.getCenter(new THREE.Vector3());
  root.position.sub(initialCenter);
  root.updateMatrixWorld(true);
  const centeredBox = new THREE.Box3().setFromObject(root);
  root.position.y -= centeredBox.min.y;
  root.updateMatrixWorld(true);

  const box = new THREE.Box3().setFromObject(root);
  const size = box.getSize(new THREE.Vector3());
  const center = box.getCenter(new THREE.Vector3());
  const radius = Math.max(size.length() * 0.5, 0.001);
  const verticalFov = THREE.MathUtils.degToRad(runtime.camera.fov);
  const horizontalFov = 2 * Math.atan(Math.tan(verticalFov / 2) * runtime.camera.aspect);
  const horizontalSpan = Math.max(size.x, size.z);

  const longestHorizontalIsX = size.x >= size.z;
  const weaponLike = horizontalSpan > Math.max(size.y, Math.min(size.x, size.z)) * 2.25;
  const direction = weaponLike
    ? new THREE.Vector3(longestHorizontalIsX ? 0.12 : 1, 0.32, longestHorizontalIsX ? 1 : 0.12)
    : new THREE.Vector3(1, 0.62, 1);
  direction.normalize();

  // Fit all eight box corners in the actual angled camera projection. This
  // avoids shrinking long rifles to a bounding sphere while still keeping
  // tall/deep structures inside the viewport.
  const right = new THREE.Vector3().crossVectors(
    new THREE.Vector3(0, 1, 0),
    direction,
  ).normalize();
  const viewUp = new THREE.Vector3().crossVectors(direction, right).normalize();
  const half = size.clone().multiplyScalar(0.5);
  const tanHorizontal = Math.tan(horizontalFov / 2);
  const tanVertical = Math.tan(verticalFov / 2);
  let fittedDistance = radius;
  for (const sx of [-1, 1]) {
    for (const sy of [-1, 1]) {
      for (const sz of [-1, 1]) {
        const offset = new THREE.Vector3(sx * half.x, sy * half.y, sz * half.z);
        const towardCamera = offset.dot(direction);
        fittedDistance = Math.max(
          fittedDistance,
          Math.abs(offset.dot(right)) / tanHorizontal + towardCamera,
          Math.abs(offset.dot(viewUp)) / tanVertical + towardCamera,
        );
      }
    }
  }
  const distance = fittedDistance * 1.18;

  runtime.camera.position.copy(center).addScaledVector(direction, distance);
  runtime.camera.near = Math.max(distance / 800, 0.001);
  runtime.camera.far = Math.max(distance * 15, radius * 30);
  runtime.camera.updateProjectionMatrix();
  runtime.controls.target.copy(center);
  runtime.controls.minDistance = Math.max(radius * 0.28, 0.01);
  runtime.controls.maxDistance = Math.max(radius * 10, distance * 3);
  runtime.controls.update();
  runtime.controls.saveState();

  runtime.ground.scale.setScalar(Math.max(radius * 0.85, 0.2));
  runtime.ground.position.set(center.x, 0, center.z);
  runtime.ground.visible = true;
  runtime.key.shadow.camera.left = -radius * 2;
  runtime.key.shadow.camera.right = radius * 2;
  runtime.key.shadow.camera.top = radius * 2;
  runtime.key.shadow.camera.bottom = -radius * 2;
  runtime.key.shadow.camera.near = 0.01;
  runtime.key.shadow.camera.far = radius * 12;
  runtime.key.shadow.camera.updateProjectionMatrix();

  return { box, size, center, radius };
}

function sceneMetrics(root, gltf, metadata, bounds, loadMs, runtime) {
  let triangles = 0;
  let meshes = 0;
  const materials = new Set();
  const textures = new Set();
  root.traverse((object) => {
    if (!object.isMesh) return;
    meshes += 1;
    const geometry = object.geometry;
    triangles += geometry.index
      ? geometry.index.count / 3
      : (geometry.attributes.position?.count || 0) / 3;
    const objectMaterials = Array.isArray(object.material) ? object.material : [object.material];
    for (const material of objectMaterials) {
      if (!material) continue;
      materials.add(material.uuid);
      for (const value of Object.values(material)) {
        if (value?.isTexture) textures.add(value.uuid);
      }
    }
  });
  return {
    object: metadata.object_name,
    asset: metadata.asset_id,
    bytes: metadata.size_bytes.toLocaleString(),
    bounds: `${bounds.size.x.toFixed(2)} × ${bounds.size.y.toFixed(2)} × ${bounds.size.z.toFixed(2)}`,
    meshes,
    triangles: Math.round(triangles).toLocaleString(),
    materials: materials.size,
    textures: textures.size,
    animations: gltf.animations?.length || 0,
    extensions: metadata.extensions.join(', ') || 'none',
    load: `${Math.round(loadMs)} ms`,
    renderer: runtime.renderer.info.render.calls >= 0
      ? runtime.renderer.capabilities.isWebGL2 ? 'WebGL 2' : 'WebGL 1'
      : 'unknown',
  };
}

function showDiagnostics(metrics) {
  elements.diagnosticsList.replaceChildren();
  for (const [label, value] of Object.entries(metrics)) {
    const term = document.createElement('dt');
    const description = document.createElement('dd');
    term.textContent = label;
    description.textContent = String(value);
    elements.diagnosticsList.append(term, description);
  }
  elements.diagnostics.hidden = false;
}

async function fetchBinary(path) {
  const response = await fetch(apiUrl(path), {
    signal: activityAbort.signal,
    headers: { Accept: 'application/octet-stream' },
  });
  if (!response.ok) throw new Error(`Terrain download failed (HTTP ${response.status}).`);
  return response.arrayBuffer();
}

function decodeTerrainChunk(buffer, metadata) {
  const view = new DataView(buffer);
  const magic = String.fromCharCode(...new Uint8Array(buffer, 0, 8));
  if (magic !== 'MEGTRN1\0') throw new Error('The terrain chunk has an invalid signature.');
  const version = view.getUint16(8, true);
  const samples = view.getUint16(10, true);
  if (version !== 1 || samples !== metadata.samples || samples < 3 || samples > 257) {
    throw new Error('The terrain chunk schema does not match its manifest.');
  }
  const expected = 28 + samples * samples * 8;
  if (buffer.byteLength !== expected || buffer.byteLength !== metadata.bytes) {
    throw new Error('The terrain chunk has the wrong size.');
  }
  const minX = view.getFloat32(12, true);
  const minZ = view.getFloat32(16, true);
  const stepX = view.getFloat32(20, true);
  const stepZ = view.getFloat32(24, true);
  const vertexCount = samples * samples;
  const heights = new Float32Array(buffer, 28, vertexCount);
  const colors = new Uint8Array(buffer, 28 + vertexCount * 4, vertexCount * 3);
  const land = new Uint8Array(buffer, 28 + vertexCount * 7, vertexCount);
  return { samples, minX, minZ, stepX, stepZ, heights, colors, land };
}

function buildTerrainMesh(chunk, manifest, reliefScale, surfaceTexture = null) {
  const { samples, minX, minZ, stepX, stepZ, heights, colors, land } = chunk;
  const origin = manifest.coordinate_system.scene_origin_rust;
  const positions = new Float32Array(samples * samples * 3);
  const vertexColors = new Float32Array(colors.length);
  const uvs = new Float32Array(samples * samples * 2);
  const srgbToLinear = (byte) => {
    const value = byte / 255;
    return value <= 0.04045
      ? value / 12.92
      : Math.pow((value + 0.055) / 1.055, 2.4);
  };
  for (let row = 0; row < samples; row += 1) {
    for (let column = 0; column < samples; column += 1) {
      const index = row * samples + column;
      const offset = index * 3;
      positions[offset] = minX + column * stepX - origin[0];
      positions[offset + 1] = heights[index] * reliefScale;
      positions[offset + 2] = -(minZ + row * stepZ - origin[2]);
      uvs[index * 2] = column / (samples - 1);
      uvs[index * 2 + 1] = row / (samples - 1);
      const submerged = land[index] < 128;
      const red = submerged ? Math.round(colors[offset] * 0.45) : colors[offset];
      const green = submerged ? Math.round(colors[offset + 1] * 0.62) : colors[offset + 1];
      const blue = submerged
        ? Math.min(255, Math.round(colors[offset + 2] * 0.85 + 45))
        : colors[offset + 2];
      vertexColors[offset] = srgbToLinear(red);
      vertexColors[offset + 1] = srgbToLinear(green);
      vertexColors[offset + 2] = srgbToLinear(blue);
    }
  }
  const indices = new Uint32Array((samples - 1) * (samples - 1) * 6);
  let cursor = 0;
  for (let row = 0; row < samples - 1; row += 1) {
    for (let column = 0; column < samples - 1; column += 1) {
      const a = row * samples + column;
      const b = a + 1;
      const c = a + samples;
      const d = c + 1;
      indices.set([a, b, c, b, d, c], cursor);
      cursor += 6;
    }
  }
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
  geometry.setAttribute('color', new THREE.BufferAttribute(vertexColors, 3));
  geometry.setAttribute('uv', new THREE.BufferAttribute(uvs, 2));
  geometry.setIndex(new THREE.BufferAttribute(indices, 1));
  geometry.computeVertexNormals();
  geometry.computeBoundingSphere();
  const material = new THREE.MeshStandardMaterial({
    map: surfaceTexture,
    vertexColors: surfaceTexture == null,
    roughness: 0.92,
    metalness: 0,
  });
  const mesh = new THREE.Mesh(geometry, material);
  mesh.receiveShadow = false;
  return mesh;
}

function buildOceanSurface(chunk, manifest, reliefScale, material) {
  const { samples, minX, minZ, stepX, stepZ, land } = chunk;
  const origin = manifest.coordinate_system.scene_origin_rust;
  const oceanY = Number(manifest.water?.ocean_level || 0) * reliefScale + 0.08;
  const positions = new Float32Array(samples * samples * 3);
  for (let row = 0; row < samples; row += 1) {
    for (let column = 0; column < samples; column += 1) {
      const index = row * samples + column;
      const offset = index * 3;
      positions[offset] = minX + column * stepX - origin[0];
      positions[offset + 1] = oceanY;
      positions[offset + 2] = -(minZ + row * stepZ - origin[2]);
    }
  }

  // Never place the flat ocean over decoded land. A single full-size plane
  // intersects low coastal terrain after overview relief compression and
  // produces the blue inland flashes seen while orbiting the camera.
  const indices = [];
  const isOcean = (index) => land[index] < 128;
  for (let row = 0; row < samples - 1; row += 1) {
    for (let column = 0; column < samples - 1; column += 1) {
      const a = row * samples + column;
      const b = a + 1;
      const c = a + samples;
      const d = c + 1;
      if (isOcean(a) && isOcean(b) && isOcean(c)) indices.push(a, b, c);
      if (isOcean(b) && isOcean(d) && isOcean(c)) indices.push(b, d, c);
    }
  }
  const group = new THREE.Group();
  group.name = 'map-ocean';
  if (indices.length) {
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    geometry.setIndex(indices);
    geometry.computeVertexNormals();
    geometry.computeBoundingSphere();
    const inside = new THREE.Mesh(geometry, material);
    inside.renderOrder = 2;
    group.add(inside);
  }

  // Four non-overlapping strips continue the ocean beyond the decoded map
  // without putting another plane underneath its terrain or masked water.
  const xA = positions[0];
  const zA = positions[2];
  const lastOffset = (samples * samples - 1) * 3;
  const xB = positions[lastOffset];
  const zB = positions[lastOffset + 2];
  const innerMinX = Math.min(xA, xB);
  const innerMaxX = Math.max(xA, xB);
  const innerMinZ = Math.min(zA, zB);
  const innerMaxZ = Math.max(zA, zB);
  const innerWidth = Math.max(1, innerMaxX - innerMinX);
  const innerDepth = Math.max(1, innerMaxZ - innerMinZ);
  const margin = Math.max(innerWidth, innerDepth) * 5.5;
  const outerMinX = innerMinX - margin;
  const outerMaxX = innerMaxX + margin;
  const outerMinZ = innerMinZ - margin;
  const outerMaxZ = innerMaxZ + margin;
  const addStrip = (minStripX, maxStripX, minStripZ, maxStripZ) => {
    const width = maxStripX - minStripX;
    const depth = maxStripZ - minStripZ;
    if (!(width > 0 && depth > 0)) return;
    const strip = new THREE.Mesh(new THREE.PlaneGeometry(width, depth), material);
    strip.rotation.x = -Math.PI / 2;
    strip.position.set(
      (minStripX + maxStripX) / 2,
      oceanY,
      (minStripZ + maxStripZ) / 2,
    );
    strip.renderOrder = 2;
    group.add(strip);
  };
  addStrip(outerMinX, innerMinX, outerMinZ, outerMaxZ);
  addStrip(innerMaxX, outerMaxX, outerMinZ, outerMaxZ);
  addStrip(innerMinX, innerMaxX, outerMinZ, innerMinZ);
  addStrip(innerMinX, innerMaxX, innerMaxZ, outerMaxZ);
  return group;
}

function terrainHeightAtScene(chunk, manifest, sceneX, sceneZ, reliefScale = 1) {
  const origin = manifest.coordinate_system.scene_origin_rust;
  const worldX = sceneX + origin[0];
  const worldZ = origin[2] - sceneZ;
  const column = THREE.MathUtils.clamp((worldX - chunk.minX) / chunk.stepX, 0, chunk.samples - 1);
  const row = THREE.MathUtils.clamp((worldZ - chunk.minZ) / chunk.stepZ, 0, chunk.samples - 1);
  const x0 = Math.floor(column);
  const z0 = Math.floor(row);
  const x1 = Math.min(x0 + 1, chunk.samples - 1);
  const z1 = Math.min(z0 + 1, chunk.samples - 1);
  const wx = column - x0;
  const wz = row - z0;
  const at = (x, z) => chunk.heights[z * chunk.samples + x];
  const top = at(x0, z0) * (1 - wx) + at(x1, z0) * wx;
  const bottom = at(x0, z1) * (1 - wx) + at(x1, z1) * wx;
  return (top * (1 - wz) + bottom * wz) * reliefScale;
}

function addWorldPaths(runtime, manifest, terrainChunk, reliefScale, groups) {
  const palette = {
    road: 0x5a4636,
    trail: 0x8b7658,
    rail: 0x2e3435,
    river: 0x2e7894,
  };
  const ribbonMaterials = new Map(
    Object.entries(palette).map(([kind, color]) => [
      kind,
      new THREE.MeshStandardMaterial({
        color,
        roughness: kind === 'river' ? 0.34 : 0.96,
        metalness: 0,
        transparent: kind === 'trail' || kind === 'river',
        opacity: kind === 'trail' ? 0.78 : kind === 'river' ? 0.88 : 1,
        depthWrite: kind !== 'river',
        side: THREE.DoubleSide,
      }),
    ]),
  );
  const fallbackMaterial = new THREE.LineBasicMaterial({ color: 0x69594a });
  let count = 0;
  for (const path of manifest.paths || []) {
    if (!Array.isArray(path.nodes) || path.nodes.length < 2) continue;
    const parent = path.kind === 'river'
      ? groups.rivers
      : path.kind === 'rail'
        ? groups.railways
        : groups.roads;
    if (ribbonMaterials.has(path.kind)) {
      const nodes = path.nodes.map((node) => new THREE.Vector3(
        Number(node[0] || 0),
        0,
        Number(node[2] || 0),
      ));
      const positions = new Float32Array(nodes.length * 2 * 3);
      const indices = new Uint32Array((nodes.length - 1) * 6);
      const width = THREE.MathUtils.clamp(
        Number(path.width || (path.kind === 'trail' ? 5 : 9))
          + (path.kind === 'rail' ? 4 : 0),
        path.kind === 'trail' ? 3 : 5,
        path.kind === 'river' ? 80 : 36,
      );
      for (let index = 0; index < nodes.length; index += 1) {
        const previous = nodes[Math.max(0, index - 1)];
        const next = nodes[Math.min(nodes.length - 1, index + 1)];
        const dx = next.x - previous.x;
        const dz = next.z - previous.z;
        const length = Math.max(0.001, Math.hypot(dx, dz));
        const offsetX = (-dz / length) * width * 0.5;
        const offsetZ = (dx / length) * width * 0.5;
        for (const side of [0, 1]) {
          const direction = side === 0 ? -1 : 1;
          const x = nodes[index].x + offsetX * direction;
          const z = nodes[index].z + offsetZ * direction;
          const cursor = (index * 2 + side) * 3;
          positions[cursor] = x;
          positions[cursor + 1] = terrainHeightAtScene(
            terrainChunk,
            manifest,
            x,
            z,
            reliefScale,
          ) + (path.kind === 'river' ? 0.45 : 0.75);
          positions[cursor + 2] = z;
        }
      }
      for (let index = 0; index < nodes.length - 1; index += 1) {
        const a = index * 2;
        indices.set([a, a + 2, a + 1, a + 1, a + 2, a + 3], index * 6);
      }
      const geometry = new THREE.BufferGeometry();
      geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
      geometry.setIndex(new THREE.BufferAttribute(indices, 1));
      geometry.computeVertexNormals();
      const ribbon = new THREE.Mesh(geometry, ribbonMaterials.get(path.kind));
      ribbon.renderOrder = path.kind === 'river' ? 5 : 6;
      parent.add(ribbon);
    } else {
      const points = path.nodes.map((node) => {
        const x = Number(node[0] || 0);
        const z = Number(node[2] || 0);
        return new THREE.Vector3(
          x,
          terrainHeightAtScene(terrainChunk, manifest, x, z, reliefScale) + 1.1,
          z,
        );
      });
      parent.add(new THREE.Line(new THREE.BufferGeometry().setFromPoints(points), fallbackMaterial));
    }
    count += 1;
  }
  if (count === 0) {
    for (const material of ribbonMaterials.values()) material.dispose();
    fallbackMaterial.dispose();
  }
  return count;
}

function markerMaterial(color) {
  const canvas = document.createElement('canvas');
  canvas.width = 96;
  canvas.height = 96;
  const context = canvas.getContext('2d');
  context.clearRect(0, 0, 96, 96);
  context.beginPath();
  context.arc(48, 48, 38, 0, Math.PI * 2);
  context.fillStyle = color;
  context.fill();
  context.lineWidth = 7;
  context.strokeStyle = '#fff7df';
  context.stroke();
  context.fillStyle = '#fff7df';
  context.fillRect(29, 45, 38, 23);
  context.fillRect(35, 35, 26, 10);
  context.fillRect(27, 68, 42, 6);
  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.needsUpdate = true;
  return new THREE.SpriteMaterial({
    map: texture,
    transparent: true,
    depthTest: false,
    depthWrite: false,
  });
}

function labelSprite(name, span) {
  const canvas = document.createElement('canvas');
  canvas.width = 256;
  canvas.height = 64;
  const context = canvas.getContext('2d');
  context.font = '700 24px Inter, system-ui, sans-serif';
  context.textAlign = 'center';
  context.textBaseline = 'middle';
  const label = String(name || 'Unknown monument').slice(0, 54);
  const width = Math.min(246, Math.max(80, context.measureText(label).width + 24));
  const left = (canvas.width - width) / 2;
  context.fillStyle = 'rgba(7, 21, 25, 0.86)';
  context.strokeStyle = 'rgba(255, 247, 223, 0.82)';
  context.lineWidth = 3;
  context.beginPath();
  context.roundRect(left, 8, width, 48, 10);
  context.fill();
  context.stroke();
  context.fillStyle = '#fff7df';
  context.fillText(label, canvas.width / 2, 33, width - 16);
  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  const material = new THREE.SpriteMaterial({
    map: texture,
    transparent: true,
    depthTest: false,
    depthWrite: false,
  });
  const sprite = new THREE.Sprite(material);
  const worldWidth = Math.max(92, Math.min(230, span * 0.052));
  sprite.scale.set(worldWidth, worldWidth * (canvas.height / canvas.width), 1);
  sprite.renderOrder = 19;
  return sprite;
}


function teamIntelDeathInitial(name) {
  const match = String(name || '').trim().match(/[a-z0-9]/i);
  return match ? match[0].toUpperCase() : '?';
}

function teamIntelMarkerMaterial(kind, markerText = '') {
  const canvas = document.createElement('canvas');
  canvas.width = 96;
  canvas.height = 96;
  const context = canvas.getContext('2d');
  context.clearRect(0, 0, 96, 96);
  context.beginPath();
  context.arc(48, 48, 37, 0, Math.PI * 2);
  context.fillStyle = kind === 'death' ? '#ca4d47' : '#35b99a';
  context.fill();
  context.lineWidth = 7;
  context.strokeStyle = '#f5fbf9';
  context.stroke();

  context.strokeStyle = '#f5fbf9';
  context.fillStyle = '#f5fbf9';
  context.lineWidth = 7;
  context.lineCap = 'round';
  context.lineJoin = 'round';
  if (kind === 'death') {
    context.font = '800 42px Inter, system-ui, sans-serif';
    context.textAlign = 'center';
    context.textBaseline = 'middle';
    context.fillText(String(markerText || '?').slice(0, 1).toUpperCase(), 48, 50);
  } else {
    // Neutral teammate silhouette: TeamInfo does not expose facing direction.
    context.beginPath();
    context.arc(48, 37, 10, 0, Math.PI * 2);
    context.fill();
    context.beginPath();
    context.roundRect(31, 51, 34, 18, 9);
    context.fill();
  }

  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.needsUpdate = true;
  return new THREE.SpriteMaterial({
    map: texture,
    transparent: true,
    depthTest: false,
    depthWrite: false,
  });
}

function teamIntelLabelSprite(name, span) {
  const canvas = document.createElement('canvas');
  canvas.width = 256;
  canvas.height = 64;
  const context = canvas.getContext('2d');
  context.font = '800 23px Inter, system-ui, sans-serif';
  context.textAlign = 'center';
  context.textBaseline = 'middle';
  const label = String(name || 'Teammate').slice(0, 42);
  const width = Math.min(246, Math.max(84, context.measureText(label).width + 24));
  const left = (canvas.width - width) / 2;
  context.fillStyle = 'rgba(5, 31, 31, 0.9)';
  context.strokeStyle = 'rgba(133, 240, 211, 0.88)';
  context.lineWidth = 3;
  context.beginPath();
  context.roundRect(left, 8, width, 48, 10);
  context.fill();
  context.stroke();
  context.fillStyle = '#effcf8';
  context.fillText(label, canvas.width / 2, 33, width - 16);
  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  const material = new THREE.SpriteMaterial({
    map: texture,
    transparent: true,
    depthTest: false,
    depthWrite: false,
  });
  const sprite = new THREE.Sprite(material);
  const worldWidth = Math.max(76, Math.min(190, span * 0.042));
  sprite.scale.set(worldWidth, worldWidth * (canvas.height / canvas.width), 1);
  sprite.renderOrder = 31;
  return sprite;
}

function disposeDetachedSprite(sprite) {
  if (!sprite) return;
  sprite.parent?.remove(sprite);
  const materials = Array.isArray(sprite.material) ? sprite.material : [sprite.material];
  for (const material of materials) {
    if (!material) continue;
    material.map?.dispose?.();
    material.dispose?.();
  }
  sprite.geometry?.dispose?.();
}

function rustPlusMapToScene(manifest, rustPlusX, rustPlusY) {
  // Rust+ TeamInfo uses 0..world_size map coordinates. world3d uses Rust's
  // centred world coordinates, then mirrors Rust Z into Three.js scene Z.
  const worldSize = Number(manifest.world_size || 0);
  const origin = manifest.coordinate_system?.scene_origin_rust;
  if (!(worldSize > 0) || !Array.isArray(origin) || origin.length < 3) return null;
  const x = Number(rustPlusX);
  const y = Number(rustPlusY);
  if (!Number.isFinite(x) || !Number.isFinite(y) || x < 0 || y < 0 || x > worldSize || y > worldSize) {
    return null;
  }
  const half = worldSize / 2;
  const worldX = x - half;
  const worldZ = y - half;
  return new THREE.Vector3(
    worldX - Number(origin[0] || 0),
    0,
    -(worldZ - Number(origin[2] || 0)),
  );
}

function rustWorldToScene(manifest, worldX, worldY, worldZ) {
  const origin = manifest.coordinate_system?.scene_origin_rust;
  if (!Array.isArray(origin) || origin.length < 3) return null;
  const x = Number(worldX);
  const y = Number(worldY);
  const z = Number(worldZ);
  if (![x, y, z].every(Number.isFinite)) return null;
  return new THREE.Vector3(
    x - Number(origin[0] || 0),
    y - Number(origin[1] || 0),
    -(z - Number(origin[2] || 0)),
  );
}

function rustWorldGridLabel(manifest, worldX, worldZ) {
  const worldSize = Number(manifest.world_size || 0);
  const cellSize = Number(manifest.grid?.cell_size_metres || 150);
  const x = Number(worldX);
  const z = Number(worldZ);
  if (!(worldSize > 0) || !(cellSize > 0) || !Number.isFinite(x) || !Number.isFinite(z)) return '';
  const cells = Math.max(1, Math.ceil(worldSize / cellSize));
  const column = THREE.MathUtils.clamp(Math.floor((x + worldSize / 2) / cellSize), 0, cells - 1);
  const row = THREE.MathUtils.clamp(Math.floor((worldSize / 2 - z) / cellSize), 0, cells - 1);
  return `${gridColumnLabel(column)}${row}`;
}

function securityMarkerMaterial(kind) {
  const normalized = String(kind || 'camera').toLowerCase();
  const turret = normalized === 'turret';
  const drone = normalized === 'drone';
  const canvas = document.createElement('canvas');
  canvas.width = 112;
  canvas.height = 112;
  const context = canvas.getContext('2d');
  context.clearRect(0, 0, 112, 112);
  context.beginPath();
  context.arc(56, 56, 43, 0, Math.PI * 2);
  context.fillStyle = turret ? '#e46e35' : (drone ? '#a06ee1' : '#3b9ee8');
  context.fill();
  context.lineWidth = 7;
  context.strokeStyle = '#f8fcff';
  context.stroke();
  context.strokeStyle = '#ffffff';
  context.fillStyle = '#ffffff';
  context.lineWidth = 7;
  context.lineCap = 'round';
  context.lineJoin = 'round';

  if (turret) {
    context.beginPath();
    context.arc(56, 48, 16, 0, Math.PI * 2);
    context.stroke();
    context.beginPath();
    context.moveTo(56, 25);
    context.lineTo(56, 34);
    context.moveTo(56, 62);
    context.lineTo(56, 76);
    context.moveTo(33, 48);
    context.lineTo(42, 48);
    context.moveTo(70, 48);
    context.lineTo(86, 48);
    context.stroke();
    context.fillRect(47, 73, 18, 9);
  } else if (drone) {
    context.beginPath();
    context.moveTo(38, 56);
    context.lineTo(74, 56);
    context.moveTo(45, 45);
    context.lineTo(67, 67);
    context.moveTo(67, 45);
    context.lineTo(45, 67);
    context.stroke();
    for (const [x, y] of [[36, 43], [76, 43], [36, 69], [76, 69]]) {
      context.beginPath();
      context.arc(x, y, 8, 0, Math.PI * 2);
      context.stroke();
    }
  } else {
    context.beginPath();
    context.roundRect(31, 42, 48, 32, 7);
    context.stroke();
    context.beginPath();
    context.arc(55, 58, 10, 0, Math.PI * 2);
    context.stroke();
    context.beginPath();
    context.moveTo(79, 49);
    context.lineTo(89, 43);
    context.lineTo(89, 69);
    context.lineTo(79, 64);
    context.closePath();
    context.stroke();
  }

  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.needsUpdate = true;
  return new THREE.SpriteMaterial({
    map: texture,
    transparent: true,
    depthTest: false,
    depthWrite: false,
  });
}

function securityLabelSprite(name, kind, span) {
  const normalized = String(kind || 'camera').toLowerCase();
  const canvas = document.createElement('canvas');
  canvas.width = 288;
  canvas.height = 64;
  const context = canvas.getContext('2d');
  context.font = '800 22px Inter, system-ui, sans-serif';
  context.textAlign = 'center';
  context.textBaseline = 'middle';
  const label = String(name || 'Security device').slice(0, 42);
  const width = Math.min(278, Math.max(92, context.measureText(label).width + 28));
  const left = (canvas.width - width) / 2;
  context.fillStyle = 'rgba(6, 24, 34, 0.92)';
  context.strokeStyle = normalized === 'turret'
    ? 'rgba(242, 139, 76, 0.92)'
    : (normalized === 'drone' ? 'rgba(188, 139, 241, 0.9)' : 'rgba(106, 195, 255, 0.92)');
  context.lineWidth = 3;
  context.beginPath();
  context.roundRect(left, 8, width, 48, 10);
  context.fill();
  context.stroke();
  context.fillStyle = '#eff9ff';
  context.fillText(label, canvas.width / 2, 33, width - 16);
  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  const material = new THREE.SpriteMaterial({
    map: texture,
    transparent: true,
    depthTest: false,
    depthWrite: false,
  });
  const sprite = new THREE.Sprite(material);
  const worldWidth = Math.max(82, Math.min(205, span * 0.045));
  sprite.scale.set(worldWidth, worldWidth * (canvas.height / canvas.width), 1);
  sprite.renderOrder = 35;
  return sprite;
}

function securityDeviceDetail(device, manifest, nowSeconds) {
  const parts = [];
  const position = Array.isArray(device?.position) ? device.position : null;
  if (position?.length >= 3) {
    const grid = rustWorldGridLabel(manifest, position[0], position[2]);
    if (grid) parts.push(grid);
    parts.push(`X ${Math.round(Number(position[0]))} · Z ${Math.round(Number(position[2]))}`);
    if (Number.isFinite(Number(position[1]))) parts.push(`elev ${Math.round(Number(position[1]))}m`);
  }
  if (Array.isArray(device?.rotation) && device.rotation.length >= 2 && Number.isFinite(Number(device.rotation[1]))) {
    const yaw = ((Number(device.rotation[1]) % 360) + 360) % 360;
    parts.push(`facing ${Math.round(yaw)}°`);
  }
  if (device?.vertical_fov) parts.push(`FOV ${Math.round(Number(device.vertical_fov))}°`);
  if (device?.last_seen) parts.push(`seen ${compactDuration(nowSeconds - Number(device.last_seen))} ago`);
  if (device?.view_available === false) {
    parts.push(device?.view_reason === 'paired_player_online' ? 'view paused while paired player is in-game' : 'view unavailable');
  }
  return parts.join(' · ');
}

function securityFacingMesh(device, anchor, span) {
  const rotation = Array.isArray(device?.rotation) ? device.rotation : null;
  if (!rotation || rotation.length < 2 || !Number.isFinite(Number(rotation[1]))) return null;
  const kind = String(device?.kind || 'camera').toLowerCase();
  const yaw = THREE.MathUtils.degToRad(Number(rotation[1]));
  const verticalFov = THREE.MathUtils.degToRad(
    THREE.MathUtils.clamp(Number(device?.vertical_fov || 55), 10, 120),
  );
  const width = Math.max(1, Number(device?.width || 160));
  const height = Math.max(1, Number(device?.height || 90));
  const horizontalFov = 2 * Math.atan(Math.tan(verticalFov / 2) * (width / height));
  const halfAngle = THREE.MathUtils.clamp(horizontalFov / 2, THREE.MathUtils.degToRad(8), THREE.MathUtils.degToRad(70));
  const length = Math.max(42, Math.min(145, span * 0.032));
  // Unity/Rust yaw 0 points along +Z. world3d mirrors Rust Z into scene Z.
  const sceneDirection = (angle) => new THREE.Vector2(Math.sin(angle), -Math.cos(angle));
  const center = sceneDirection(yaw);
  const left = sceneDirection(yaw - halfAngle);
  const right = sceneDirection(yaw + halfAngle);
  const positions = new Float32Array([
    0, 0, 0,
    left.x * length, 0, left.y * length,
    right.x * length, 0, right.y * length,
  ]);
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
  geometry.setIndex([0, 1, 2]);
  geometry.computeVertexNormals();
  const color = kind === 'turret' ? 0xe46e35 : (kind === 'drone' ? 0xa06ee1 : 0x3b9ee8);
  const material = new THREE.MeshBasicMaterial({
    color,
    transparent: true,
    opacity: kind === 'turret' ? 0.18 : 0.14,
    side: THREE.DoubleSide,
    depthTest: false,
    depthWrite: false,
  });
  const mesh = new THREE.Mesh(geometry, material);
  mesh.position.set(anchor.x, anchor.y + 0.7, anchor.z);
  mesh.renderOrder = 27;
  mesh.userData.securityFacing = true;
  mesh.userData.securityKind = kind;

  // A short centre ray remains visible even when the transparent cone crosses a
  // bright biome or road texture.
  const rayGeometry = new THREE.BufferGeometry().setFromPoints([
    new THREE.Vector3(0, 0.2, 0),
    new THREE.Vector3(center.x * length, 0.2, center.y * length),
  ]);
  const rayMaterial = new THREE.LineBasicMaterial({
    color,
    transparent: true,
    opacity: 0.9,
    depthTest: false,
    depthWrite: false,
  });
  const ray = new THREE.Line(rayGeometry, rayMaterial);
  ray.renderOrder = 28;
  mesh.add(ray);
  return mesh;
}

function compactDuration(seconds) {
  const value = Math.max(0, Math.floor(Number(seconds) || 0));
  if (value < 60) return `${value}s`;
  if (value < 3600) return `${Math.floor(value / 60)}m`;
  if (value < 86400) return `${Math.floor(value / 3600)}h ${Math.floor((value % 3600) / 60)}m`;
  return `${Math.floor(value / 86400)}d ${Math.floor((value % 86400) / 3600)}h`;
}

function teamIntelMemberDetail(member, nowSeconds) {
  const parts = [];
  if (member.grid) parts.push(String(member.grid));
  if (member.alive_since) parts.push(`alive ${compactDuration(nowSeconds - Number(member.alive_since))}`);
  if (member.afk) parts.push(`AFK ${compactDuration(member.afk_seconds)}`);
  const deaths = Math.max(0, Number(member.death_count) || 0);
  parts.push(`${deaths} death${deaths === 1 ? '' : 's'} this wipe`);
  return parts.join(' · ');
}

function teamIntelDeathDetail(death, nowSeconds) {
  const parts = [];
  if (death.grid) parts.push(String(death.grid));
  if (death.died_at) parts.push(`died ${compactDuration(nowSeconds - Number(death.died_at))} ago`);
  return parts.join(' · ');
}

function setTeamIntelPickData(object, kind, title, detail, memberId = '') {
  object.userData.teamIntel = true;
  object.userData.teamIntelKind = kind;
  object.userData.teamIntelTitle = String(title || '').slice(0, 80);
  object.userData.teamIntelDetail = String(detail || '').slice(0, 220);
  object.userData.teamIntelMemberId = String(memberId || '').slice(0, 40);
}

function setSecurityPickData(object, device) {
  object.userData.securityDevice = true;
  object.userData.securityDeviceId = String(device?.id || '').slice(0, 40);
  object.userData.securityKind = String(device?.kind || 'camera').slice(0, 16);
  object.userData.securityTitle = String(device?.name || 'Security device').slice(0, 80);
  object.userData.securityDetail = String(device?._detail || '').slice(0, 260);
  object.userData.securityViewAvailable = device?.view_available !== false;
  object.userData.securityViewReason = String(device?.view_reason || '').slice(0, 48);
}

function configureTeamIntel(runtime, metadata, manifest, terrainChunk, span, reliefScale, teamGroup, deathGroup, trailGroup, cameraGroup, turretGroup, securityFacingGroup) {
  const endpoint = metadata.team_intel_url;
  const trailEndpoint = metadata.team_trails_url;
  const securityFrameBase = metadata.security_frame_base_url;
  runtime.securityFrameAvailable = Boolean(securityFrameBase);
  if (!endpoint) {
    elements.team.hidden = true;
    elements.teamFilterButton.hidden = true;
    elements.teamFilterPanel.hidden = true;
    elements.deaths.hidden = true;
    elements.deathFilterButton.hidden = true;
    elements.deathFilterPanel.hidden = true;
    elements.trails.hidden = true;
    elements.trailFilterButton.hidden = true;
    elements.trailFilterPanel.hidden = true;
    elements.trailRangePicker.hidden = true;
    elements.cameras.hidden = true;
    elements.turrets.hidden = true;
    elements.securityFacing.hidden = true;
    elements.securityViewerPanel.hidden = true;
    elements.selectionSecurityViewButton.hidden = true;
    elements.teamIntelPanel.hidden = true;
    return;
  }

  elements.team.hidden = false;
  elements.teamFilterButton.hidden = true;
  elements.teamFilterPanel.hidden = true;
  elements.teamFilterButton.setAttribute('aria-expanded', 'false');
  elements.deaths.hidden = false;
  elements.deathFilterButton.hidden = true;
  elements.deathFilterPanel.hidden = true;
  elements.deathFilterButton.setAttribute('aria-expanded', 'false');
  const trailsAvailable = Boolean(trailEndpoint);
  elements.trails.hidden = !trailsAvailable;
  elements.trailFilterButton.hidden = true;
  elements.trailFilterPanel.hidden = true;
  elements.trailFilterButton.setAttribute('aria-expanded', 'false');
  elements.trailRangePicker.hidden = !trailsAvailable;
  elements.trailRangeSelect.value = '15m';
  elements.trailFadeToggle.checked = true;
  elements.cameras.hidden = true;
  elements.turrets.hidden = true;
  elements.securityFacing.hidden = true;
  elements.securityViewerPanel.hidden = true;
  elements.selectionSecurityViewButton.hidden = true;
  elements.teamIntelPanel.hidden = false;
  elements.teamIntelSummary.textContent = 'Connecting to MEG team tracker…';
  elements.teamIntelDetail.textContent = 'Read-only · no extra Rust+ polling';

  const teamMaterial = teamIntelMarkerMaterial('team');
  const teamSize = Math.max(26, Math.min(66, span * 0.014));
  const deathSize = Math.max(24, Math.min(58, span * 0.012));
  const securitySize = Math.max(28, Math.min(64, span * 0.0135));
  const teamEntries = new Map();
  let baseEntry = null;
  const teamFilterPlayers = new Map();
  const teamFilterCheckboxes = new Map();
  const activeTeamMembers = new Set();
  const deathEntries = new Map();
  const deathFilterPlayers = new Map();
  const deathFilterCheckboxes = new Map();
  const activeDeathMembers = new Set();
  const trailFilterPlayers = new Map();
  const trailFilterCheckboxes = new Map();
  const activeTrailMembers = new Set();
  const trailEntries = new Map();
  const securityEntries = new Map();
  const securityFacingEntries = new Map();
  let currentMemberIdByName = new Map();
  let teamFilterAllMode = true;
  let deathFilterAllMode = true;
  let trailFilterAllMode = true;
  let lastTeamFilterSignature = '';
  let lastDeathFilterSignature = '';
  let lastTrailFilterSignature = '';
  let lastLiveCount = 0;
  let lastOnlineCount = 0;
  let stopped = false;
  let timer = null;
  let trailTimer = null;
  let trailRequestVersion = 0;
  let trailRangeKey = '15m';
  let trailRangeLabel = 'Last 15 min';
  let lastTrailPointCount = 0;
  let lastCameraCount = 0;
  let lastTurretCount = 0;
  let securityViewerTimer = null;
  let securityViewerRequestVersion = 0;
  let securityViewerBlobUrl = '';
  let activeSecurityViewerDevice = null;
  let securityViewerAbort = null;
  let securityViewerEtag = '';
  let securityViewerUnchangedCount = 0;

  const normalizedPlayerName = (name) => String(name || '').trim().toLocaleLowerCase();
  const deathMemberKey = (death) => {
    const memberId = String(death?.member_id || '').trim();
    if (memberId) return memberId;
    const normalized = normalizedPlayerName(death?.name);
    if (!normalized) return '';
    return currentMemberIdByName.get(normalized) || `legacy-name:${normalized}`;
  };
  const recentDeathCandidates = (deaths) => deaths
    .filter((death) => death?.id && death.has_position)
    .slice(-48);

  const removeTeamEntry = (entry) => {
    if (!entry) return;
    entry.icon.parent?.remove(entry.icon);
    disposeDetachedSprite(entry.label);
  };
  const updateBase = (base) => {
    const signature = base ? `${base.x}:${base.y}:${base.radius}` : '';
    if (baseEntry?.signature === signature) return;
    if (baseEntry) {
      disposeDetachedSprite(baseEntry.label);
      baseEntry.ring.parent?.remove(baseEntry.ring);
      baseEntry.ring.geometry.dispose();
      baseEntry.ring.material.dispose();
      baseEntry = null;
    }
    if (!base || !Number.isFinite(Number(base.radius)) || Number(base.radius) <= 0) return;
    const centre = rustPlusMapToScene(manifest, base.x, base.y);
    if (!centre) return;
    centre.y = terrainHeightAtScene(terrainChunk, manifest, centre.x, centre.z, reliefScale) + teamSize;
    const label = teamIntelLabelSprite('Main base', span);
    label.position.copy(centre);
    setTeamIntelPickData(label, 'Main base', 'Main base', `Shared activity radius: ${Number(base.radius)}m`);
    const points = [];
    for (let index = 0; index <= 64; index += 1) {
      const angle = index / 64 * Math.PI * 2;
      const point = rustPlusMapToScene(manifest,
        Number(base.x) + Math.cos(angle) * Number(base.radius),
        Number(base.y) + Math.sin(angle) * Number(base.radius));
      if (!point) continue;
      point.y = terrainHeightAtScene(terrainChunk, manifest, point.x, point.z, reliefScale) + 4;
      points.push(point);
    }
    const ring = new THREE.Line(new THREE.BufferGeometry().setFromPoints(points),
      new THREE.LineBasicMaterial({ color: '#ffd36b', depthTest: false }));
    ring.renderOrder = 31;
    teamGroup.add(label, ring);
    baseEntry = { signature, label, ring };
  };
  const removeDeathEntry = (entry) => {
    if (!entry) return;
    entry.icon.parent?.remove(entry.icon);
    entry.icon.material?.map?.dispose?.();
    entry.icon.material?.dispose?.();
  };
  const removeTrailEntry = (entry) => {
    if (!entry) return;
    entry.group?.parent?.remove(entry.group);
    entry.group?.traverse?.((object) => {
      object.geometry?.dispose?.();
      const materials = Array.isArray(object.material) ? object.material : [object.material];
      for (const material of materials) material?.dispose?.();
    });
  };
  const removeSecurityEntry = (entry) => {
    if (!entry) return;
    entry.icon?.parent?.remove(entry.icon);
    entry.icon?.material?.map?.dispose?.();
    entry.icon?.material?.dispose?.();
    disposeDetachedSprite(entry.label);
  };
  const removeSecurityFacingEntry = (entry) => {
    if (!entry) return;
    entry.parent?.remove(entry);
    entry.traverse?.((object) => {
      object.geometry?.dispose?.();
      const materials = Array.isArray(object.material) ? object.material : [object.material];
      for (const material of materials) material?.dispose?.();
    });
  };

  const closeSecurityViewer = () => {
    securityViewerRequestVersion += 1;
    if (securityViewerTimer !== null) {
      clearTimeout(securityViewerTimer);
      securityViewerTimer = null;
    }
    securityViewerAbort?.abort();
    securityViewerAbort = null;
    securityViewerEtag = '';
    securityViewerUnchangedCount = 0;
    activeSecurityViewerDevice = null;
    elements.securityViewerPanel.hidden = true;
    elements.securityViewerImage.hidden = true;
    elements.securityViewerImage.removeAttribute('src');
    if (securityViewerBlobUrl) {
      URL.revokeObjectURL(securityViewerBlobUrl);
      securityViewerBlobUrl = '';
    }
    elements.securityViewerLoading.textContent = 'Opening Rust+ camera…';
  };

  const securityViewLabel = (kind) => {
    if (kind === 'turret') return 'Auto Turret camera';
    if (kind === 'drone') return 'Drone camera';
    return 'Rust+ camera';
  };

  const refreshSecurityFrame = async (version) => {
    const device = activeSecurityViewerDevice;
    if (!device || version !== securityViewerRequestVersion || runtime.isStopped() || !securityFrameBase) return;
    if (document.hidden) {
      securityViewerTimer = setTimeout(() => void refreshSecurityFrame(version), 5000);
      return;
    }
    if (securityViewerAbort) return;
    const controller = new AbortController();
    securityViewerAbort = controller;
    const frameUrl = `${securityFrameBase}/${encodeURIComponent(device.id)}/frame?viewer=${encodeURIComponent(cameraViewerId)}`;
    try {
      const response = await fetch(apiUrl(frameUrl), {
        cache: 'no-cache',
        headers: cameraRequestHeaders(securityViewerEtag),
        signal: controller.signal,
      });
      const responseEtag = String(response.headers.get('etag') || '').trim();
      if (responseEtag) securityViewerEtag = responseEtag;
      if (response.status === 304) {
        securityViewerUnchangedCount += 1;
        if (version === securityViewerRequestVersion && activeSecurityViewerDevice) {
          securityViewerTimer = setTimeout(
            () => void refreshSecurityFrame(version),
            cameraRefreshDelay({ unchangedCount: securityViewerUnchangedCount, ready: true }),
          );
        }
        return;
      }
      const contentType = String(response.headers.get('content-type') || '').toLowerCase();
      const sourceSize = String(response.headers.get('x-meg-camera-source') || '').trim();
      const displaySize = String(response.headers.get('x-meg-camera-display') || '').trim();
      const enhancement = String(response.headers.get('x-meg-camera-enhancement') || '').trim();
      const packetCount = Math.max(0, Number.parseInt(response.headers.get('x-meg-camera-packets') || '0', 10) || 0);
      const readyAt = Math.max(1, Number.parseInt(response.headers.get('x-meg-camera-ready-at') || '10', 10) || 10);
      const cameraReady = response.headers.get('x-meg-camera-ready') === '1';
      const cachedFrame = response.headers.get('x-meg-camera-cached') === '1';
      if (!response.ok || contentType.includes('application/json')) {
        let payload = {};
        try { payload = await response.json(); } catch (_) { payload = {}; }
        const reason = String(payload?.reason || 'camera_unavailable');
        const message = String(payload?.message || 'That Rust+ camera is unavailable right now.');
        elements.securityViewerImage.hidden = true;
        elements.securityViewerLoading.textContent = message;
        elements.securityViewerStatus.textContent = reason === 'paired_player_online'
          ? 'Last-known map position remains available · camera viewing resumes after the paired Rust account disconnects'
          : `Read-only · ${reason.replaceAll('_', ' ')}`;
        securityViewerTimer = setTimeout(
          () => void refreshSecurityFrame(version),
          cameraRefreshDelay({ failed: true }),
        );
        return;
      }
      const blob = await response.blob();
      if (version !== securityViewerRequestVersion || !activeSecurityViewerDevice) return;
      securityViewerUnchangedCount = 0;
      const nextUrl = URL.createObjectURL(blob);
      const previousUrl = securityViewerBlobUrl;
      securityViewerBlobUrl = nextUrl;
      elements.securityViewerImage.src = nextUrl;
      elements.securityViewerImage.hidden = false;
      elements.securityViewerLoading.textContent = '';
      if (previousUrl) URL.revokeObjectURL(previousUrl);
      const popupProgress = Math.min(packetCount, readyAt);
      const popupSource = sourceSize && sourceSize !== 'unknown' ? `${sourceSize} source` : 'Rust+ source';
      const popupDisplay = displaySize && displaySize !== 'unknown' ? ` · ${displaySize} ${enhancement === 'smooth_hd' ? 'Smooth HD' : 'display'}` : '';
      const popupQuality = cameraReady
        ? `${popupSource}${popupDisplay} · ready`
        : (packetCount > 0 ? `sampling ${popupProgress}/${readyAt} · ${popupSource}` : popupSource);
      elements.securityViewerStatus.textContent = `Live Rust+ · ${popupQuality}${cachedFrame ? ' · waiting for next sample' : ''} · read-only`;
      securityViewerTimer = setTimeout(
        () => void refreshSecurityFrame(version),
        cameraRefreshDelay({ ready: cameraReady }),
      );
    } catch (error) {
      if (isCameraAbort(error)) return;
      if (activityClosed || runtime.isStopped() || version !== securityViewerRequestVersion) return;
      elements.securityViewerLoading.textContent = elements.securityViewerImage.hidden
        ? 'MEG could not refresh that Rust+ camera frame.'
        : '';
      elements.securityViewerStatus.textContent = 'Read-only · try the camera again in a moment';
      console.warn('Security camera frame refresh failed', redactedDiagnostic(error));
      securityViewerTimer = setTimeout(
        () => void refreshSecurityFrame(version),
        cameraRefreshDelay({ failed: true }),
      );
    } finally {
      if (securityViewerAbort === controller) securityViewerAbort = null;
    }
  };

  const openSecurityViewer = (device) => {
    if (!device?.id || !securityFrameBase) return;
    securityViewerRequestVersion += 1;
    if (securityViewerTimer !== null) clearTimeout(securityViewerTimer);
    securityViewerTimer = null;
    securityViewerAbort?.abort();
    securityViewerAbort = null;
    securityViewerEtag = '';
    securityViewerUnchangedCount = 0;
    activeSecurityViewerDevice = { ...device };
    elements.securityViewerKind.textContent = securityViewLabel(String(device.kind || 'camera'));
    elements.securityViewerName.textContent = String(device.name || 'Security device').slice(0, 80);
    elements.securityViewerPanel.hidden = false;
    elements.securityViewerImage.hidden = true;
    elements.securityViewerLoading.textContent = device.view_available === false
      ? (device.view_reason === 'paired_player_online'
        ? 'Camera viewing is paused while the paired Rust account is connected to the server.'
        : 'This Rust+ camera is unavailable right now.')
      : 'Opening Rust+ camera…';
    elements.securityViewerStatus.textContent = 'Read-only · MEG never sends camera or turret controls';
    if (device.view_available !== false) void refreshSecurityFrame(securityViewerRequestVersion);
  };

  const clearTrailEntries = () => {
    for (const entry of trailEntries.values()) removeTrailEntry(entry);
    trailEntries.clear();
    lastTrailPointCount = 0;
    runtime.invalidate(2);
  };
  const clearEntries = () => {
    updateBase(null);
    for (const entry of teamEntries.values()) removeTeamEntry(entry);
    for (const entry of deathEntries.values()) removeDeathEntry(entry);
    teamEntries.clear();
    deathEntries.clear();
    clearTrailEntries();
    for (const entry of securityEntries.values()) removeSecurityEntry(entry);
    for (const entry of securityFacingEntries.values()) removeSecurityFacingEntry(entry);
    securityEntries.clear();
    securityFacingEntries.clear();
    lastCameraCount = 0;
    lastTurretCount = 0;
    closeSecurityViewer();
    runtime.invalidate(2);
  };

  const selectedTrailPlayers = () => [...trailFilterPlayers.values()]
    .filter((player) => activeTrailMembers.has(player.id));

  const refreshTeamIntelSummary = () => {
    let shownLive = 0;
    if (teamGroup.visible) {
      for (const entry of teamEntries.values()) {
        if (entry.icon.visible) shownLive += 1;
      }
    }
    const liveText = shownLive === lastLiveCount
      ? `${lastLiveCount} live on map`
      : `${shownLive} of ${lastLiveCount} live shown`;
    const totalDeaths = deathEntries.size;
    let shownDeaths = 0;
    for (const entry of deathEntries.values()) {
      if (entry.icon.visible) shownDeaths += 1;
    }
    const deathText = shownDeaths === totalDeaths
      ? `${totalDeaths} recent deaths`
      : `${shownDeaths} of ${totalDeaths} deaths shown`;
    const trailText = trailsAvailable && trailGroup.visible
      ? ` · ${lastTrailPointCount} trail points`
      : '';
    const securityParts = [];
    if (lastCameraCount > 0) securityParts.push(`${lastCameraCount} camera${lastCameraCount === 1 ? '' : 's'}`);
    if (lastTurretCount > 0) securityParts.push(`${lastTurretCount} turret${lastTurretCount === 1 ? '' : 's'}`);
    const securityText = securityParts.length ? ` · ${securityParts.join(' · ')}` : '';
    elements.teamIntelSummary.textContent = `${liveText} · ${lastOnlineCount} online · ${deathText}${trailText}${securityText}`;
  };

  const selectedTeamPlayers = () => [...teamFilterPlayers.values()]
    .filter((player) => activeTeamMembers.has(player.id));

  const syncTeamFilterUi = () => {
    const players = [...teamFilterPlayers.values()];
    const total = players.length;
    const selectedPlayers = selectedTeamPlayers();
    const selected = selectedPlayers.length;
    elements.teamFilterButton.hidden = total === 0;

    if (total === 0) {
      elements.teamFilterPanel.hidden = true;
      elements.teamFilterButton.setAttribute('aria-expanded', 'false');
      elements.teamFilterButton.textContent = 'Player Filter: All';
      elements.teamFilterSummary.textContent = 'No teammates are available for live marker filtering yet.';
      return;
    }
    if (selected === total) {
      elements.teamFilterButton.textContent = 'Player Filter: All';
      elements.teamFilterSummary.textContent = `Showing live markers for all ${total} teammate${total === 1 ? '' : 's'}.`;
    } else if (selected === 0) {
      elements.teamFilterButton.textContent = 'Player Filter: None';
      elements.teamFilterSummary.textContent = 'All live teammate markers are filtered out.';
    } else if (selected === 1) {
      const name = String(selectedPlayers[0].name || 'Teammate');
      elements.teamFilterButton.textContent = `Player Filter: ${name.slice(0, 22)}`;
      elements.teamFilterSummary.textContent = `Showing only ${name}'s live marker.`;
    } else {
      elements.teamFilterButton.textContent = `Player Filter: ${selected}/${total}`;
      elements.teamFilterSummary.textContent = `Showing live markers for ${selected} of ${total} teammates.`;
    }
    for (const [id, checkbox] of teamFilterCheckboxes) checkbox.checked = activeTeamMembers.has(id);
  };

  const applyTeamVisibility = () => {
    for (const [memberId, entry] of teamEntries) {
      const visible = teamFilterAllMode || activeTeamMembers.has(memberId);
      entry.icon.visible = visible;
      entry.label.visible = visible;
    }
    syncTeamFilterUi();
    refreshTeamIntelSummary();
    runtime.invalidate(3);
  };

  const setAllTeamFilters = (visible) => {
    activeTeamMembers.clear();
    teamFilterAllMode = visible;
    if (visible) {
      for (const id of teamFilterPlayers.keys()) activeTeamMembers.add(id);
    }
    applyTeamVisibility();
  };

  const rebuildTeamFilter = (members) => {
    const ordered = members
      .filter((member) => String(member?.id || '').trim())
      .map((member) => {
        const live = Boolean(member?.online && member?.alive && member?.has_position);
        let detail = 'Offline';
        if (live) detail = 'Live on map';
        else if (member?.online && !member?.alive) detail = 'Dead / respawning';
        else if (member?.online && !member?.has_position) detail = 'Online · no position';
        else if (member?.online) detail = 'Online';
        return {
          id: String(member.id),
          name: String(member?.name || 'Teammate').slice(0, 80),
          detail,
        };
      })
      .sort((left, right) => left.name.localeCompare(right.name, undefined, { sensitivity: 'base' }));

    teamFilterPlayers.clear();
    for (const player of ordered) teamFilterPlayers.set(player.id, player);
    if (teamFilterAllMode) {
      activeTeamMembers.clear();
      for (const id of teamFilterPlayers.keys()) activeTeamMembers.add(id);
    } else {
      for (const id of [...activeTeamMembers]) {
        if (!teamFilterPlayers.has(id)) activeTeamMembers.delete(id);
      }
      if (teamFilterPlayers.size > 0 && activeTeamMembers.size === teamFilterPlayers.size) {
        teamFilterAllMode = true;
      }
    }

    const signature = JSON.stringify(ordered.map((player) => [player.id, player.name, player.detail]));
    if (signature !== lastTeamFilterSignature) {
      lastTeamFilterSignature = signature;
      teamFilterCheckboxes.clear();
      elements.teamFilterList.replaceChildren();
      for (const player of ordered) {
        const row = document.createElement('label');
        row.className = 'team-filter-option';
        const checkbox = document.createElement('input');
        checkbox.type = 'checkbox';
        checkbox.checked = activeTeamMembers.has(player.id);
        checkbox.value = player.id;
        const copy = document.createElement('span');
        const title = document.createElement('strong');
        title.textContent = player.name;
        const detail = document.createElement('small');
        detail.textContent = player.detail;
        copy.append(title, detail);
        row.append(checkbox, copy);
        elements.teamFilterList.append(row);
        teamFilterCheckboxes.set(player.id, checkbox);
        checkbox.addEventListener('change', () => {
          if (checkbox.checked) activeTeamMembers.add(player.id);
          else activeTeamMembers.delete(player.id);
          teamFilterAllMode = teamFilterPlayers.size > 0
            && activeTeamMembers.size === teamFilterPlayers.size;
          applyTeamVisibility();
        });
      }
    }
    syncTeamFilterUi();
  };

  const syncDeathFilterUi = () => {
    const players = [...deathFilterPlayers.values()];
    const total = players.length;
    const selectedPlayers = players.filter((player) => activeDeathMembers.has(player.id));
    const selected = selectedPlayers.length;

    elements.deathFilterButton.hidden = total === 0;
    if (total === 0) {
      elements.deathFilterPanel.hidden = true;
      elements.deathFilterButton.setAttribute('aria-expanded', 'false');
      elements.deathFilterButton.textContent = 'Death Filter: All';
      elements.deathFilterSummary.textContent = 'No teammate death markers are available yet.';
      return;
    }

    if (selected === total) {
      elements.deathFilterButton.textContent = 'Death Filter: All';
      elements.deathFilterSummary.textContent = `Showing deaths for all ${total} teammate${total === 1 ? '' : 's'}.`;
    } else if (selected === 0) {
      elements.deathFilterButton.textContent = 'Death Filter: None';
      elements.deathFilterSummary.textContent = 'All teammate death markers are filtered out.';
    } else if (selected === 1) {
      const name = String(selectedPlayers[0].name || 'Teammate');
      elements.deathFilterButton.textContent = `Death Filter: ${name.slice(0, 22)}`;
      elements.deathFilterSummary.textContent = `Showing only ${name}'s death markers.`;
    } else {
      elements.deathFilterButton.textContent = `Death Filter: ${selected}/${total}`;
      elements.deathFilterSummary.textContent = `Showing death markers for ${selected} of ${total} teammates.`;
    }

    for (const [id, checkbox] of deathFilterCheckboxes) {
      checkbox.checked = activeDeathMembers.has(id);
    }
  };

  const applyDeathVisibility = () => {
    for (const entry of deathEntries.values()) {
      entry.icon.visible = deathFilterAllMode || activeDeathMembers.has(entry.memberId);
    }
    syncDeathFilterUi();
    refreshTeamIntelSummary();
    runtime.invalidate(3);
  };

  const setAllDeathFilters = (visible) => {
    activeDeathMembers.clear();
    deathFilterAllMode = visible;
    if (visible) {
      for (const id of deathFilterPlayers.keys()) activeDeathMembers.add(id);
    }
    applyDeathVisibility();
  };

  const TRAIL_RED = new THREE.Color(0xff3b30);
  const TRAIL_RED_DIM = new THREE.Color(0xd8443e);
  const TRAIL_HALO = new THREE.Color(0x260706);
  let highlightedTrailMemberId = '';

  const trailColour = () => TRAIL_RED.clone();
  const isHighlightedTrail = (memberId) => Boolean(
    highlightedTrailMemberId
    && highlightedTrailMemberId === String(memberId || '')
  );
  const highlightTrailMember = (memberId) => {
    const id = String(memberId || '').trim();
    highlightedTrailMemberId = id && trailFilterPlayers.has(id) ? id : '';
    if (trailGroup.visible && trailEntries.size > 0) void refreshTrails();
  };
  runtime.highlightTrailMember = highlightTrailMember;

  const syncTrailFilterUi = () => {
    if (!trailsAvailable) return;
    const players = [...trailFilterPlayers.values()];
    const total = players.length;
    const selectedPlayers = selectedTrailPlayers();
    const selected = selectedPlayers.length;
    elements.trailFilterButton.hidden = total === 0;

    if (total === 0) {
      elements.trailFilterPanel.hidden = true;
      elements.trailFilterButton.setAttribute('aria-expanded', 'false');
      elements.trailFilterButton.textContent = 'Trail Filter: All';
      elements.trailFilterSummary.textContent = 'No teammates are available for trail filtering yet.';
      return;
    }
    if (selected === total) {
      elements.trailFilterButton.textContent = 'Trail Filter: All';
      elements.trailFilterSummary.textContent = `Showing ${trailRangeLabel.toLowerCase()} for all ${total} teammate${total === 1 ? '' : 's'}.`;
    } else if (selected === 0) {
      elements.trailFilterButton.textContent = 'Trail Filter: None';
      elements.trailFilterSummary.textContent = 'All teammate trails are filtered out.';
    } else if (selected === 1) {
      const name = String(selectedPlayers[0].name || 'Teammate');
      elements.trailFilterButton.textContent = `Trail Filter: ${name.slice(0, 22)}`;
      elements.trailFilterSummary.textContent = `Showing only ${name}'s ${trailRangeLabel.toLowerCase()} trail.`;
    } else {
      elements.trailFilterButton.textContent = `Trail Filter: ${selected}/${total}`;
      elements.trailFilterSummary.textContent = `Showing ${trailRangeLabel.toLowerCase()} trails for ${selected} of ${total} teammates.`;
    }
    for (const [id, checkbox] of trailFilterCheckboxes) checkbox.checked = activeTrailMembers.has(id);
  };

  const applyTrailVisibility = () => {
    for (const [memberId, entry] of trailEntries) {
      entry.group.visible = trailFilterAllMode || activeTrailMembers.has(memberId);
    }
    syncTrailFilterUi();
    refreshTeamIntelSummary();
    runtime.invalidate(3);
  };

  const setAllTrailFilters = (visible) => {
    activeTrailMembers.clear();
    trailFilterAllMode = visible;
    if (visible) {
      for (const id of trailFilterPlayers.keys()) activeTrailMembers.add(id);
    } else {
      highlightedTrailMemberId = '';
    }
    applyTrailVisibility();
    if (trailGroup.visible) void refreshTrails();
  };

  const rebuildTrailFilter = (members) => {
    if (!trailsAvailable) return;
    const ordered = members
      .filter((member) => String(member?.id || '').trim())
      .map((member) => ({
        id: String(member.id),
        name: String(member?.name || 'Teammate').slice(0, 80),
      }))
      .sort((left, right) => left.name.localeCompare(right.name, undefined, { sensitivity: 'base' }));
    trailFilterPlayers.clear();
    for (const player of ordered) trailFilterPlayers.set(player.id, player);

    if (trailFilterAllMode) {
      activeTrailMembers.clear();
      for (const id of trailFilterPlayers.keys()) activeTrailMembers.add(id);
    } else {
      for (const id of [...activeTrailMembers]) {
        if (!trailFilterPlayers.has(id)) activeTrailMembers.delete(id);
      }
      if (trailFilterPlayers.size > 0 && activeTrailMembers.size === trailFilterPlayers.size) {
        trailFilterAllMode = true;
      }
    }

    const signature = JSON.stringify(ordered.map((player) => [player.id, player.name]));
    if (signature !== lastTrailFilterSignature) {
      lastTrailFilterSignature = signature;
      trailFilterCheckboxes.clear();
      elements.trailFilterList.replaceChildren();
      for (const player of ordered) {
        const row = document.createElement('label');
        row.className = 'trail-filter-option';
        const checkbox = document.createElement('input');
        checkbox.type = 'checkbox';
        checkbox.checked = activeTrailMembers.has(player.id);
        checkbox.value = player.id;
        const swatch = document.createElement('i');
        const colour = trailColour(player.id);
        swatch.style.setProperty('--trail-colour', `#${colour.getHexString()}`);
        const copy = document.createElement('span');
        const title = document.createElement('strong');
        title.textContent = player.name;
        const detail = document.createElement('small');
        detail.textContent = 'Roaming path';
        copy.append(title, detail);
        row.append(checkbox, swatch, copy);
        elements.trailFilterList.append(row);
        trailFilterCheckboxes.set(player.id, checkbox);
        checkbox.addEventListener('change', () => {
          if (checkbox.checked) activeTrailMembers.add(player.id);
          else activeTrailMembers.delete(player.id);
          trailFilterAllMode = trailFilterPlayers.size > 0
            && activeTrailMembers.size === trailFilterPlayers.size;
          applyTrailVisibility();
          if (trailGroup.visible) void refreshTrails();
        });
      }
    }
    syncTrailFilterUi();
  };

  const rebuildDeathFilter = (members, deaths) => {
    currentMemberIdByName = new Map();
    for (const member of members) {
      const id = String(member?.id || '').trim();
      const normalized = normalizedPlayerName(member?.name);
      if (id && normalized) currentMemberIdByName.set(normalized, id);
    }

    const nextPlayers = new Map();
    for (const member of members) {
      const id = String(member?.id || '').trim();
      if (!id) continue;
      nextPlayers.set(id, {
        id,
        name: String(member?.name || 'Teammate').slice(0, 80),
        recentCount: 0,
        wipeCount: Math.max(0, Number(member?.death_count) || 0),
      });
    }
    for (const death of recentDeathCandidates(deaths)) {
      const id = deathMemberKey(death);
      if (!id) continue;
      const existing = nextPlayers.get(id) || {
        id,
        name: String(death?.name || 'Teammate').slice(0, 80),
        recentCount: 0,
        wipeCount: null,
      };
      existing.recentCount += 1;
      if ((!existing.name || existing.name === 'Teammate') && death?.name) {
        existing.name = String(death.name).slice(0, 80);
      }
      nextPlayers.set(id, existing);
    }

    const ordered = [...nextPlayers.values()].sort((left, right) =>
      left.name.localeCompare(right.name, undefined, { sensitivity: 'base' }));
    deathFilterPlayers.clear();
    for (const player of ordered) deathFilterPlayers.set(player.id, player);

    if (deathFilterAllMode) {
      activeDeathMembers.clear();
      for (const id of deathFilterPlayers.keys()) activeDeathMembers.add(id);
    } else {
      for (const id of [...activeDeathMembers]) {
        if (!deathFilterPlayers.has(id)) activeDeathMembers.delete(id);
      }
      if (deathFilterPlayers.size > 0 && activeDeathMembers.size === deathFilterPlayers.size) {
        deathFilterAllMode = true;
      }
    }

    const signature = JSON.stringify(ordered.map((player) => [
      player.id,
      player.name,
      player.recentCount,
      player.wipeCount,
    ]));
    if (signature !== lastDeathFilterSignature) {
      lastDeathFilterSignature = signature;
      deathFilterCheckboxes.clear();
      elements.deathFilterList.replaceChildren();
      for (const player of ordered) {
        const row = document.createElement('label');
        row.className = 'death-filter-option';
        const checkbox = document.createElement('input');
        checkbox.type = 'checkbox';
        checkbox.checked = activeDeathMembers.has(player.id);
        checkbox.value = player.id;
        const copy = document.createElement('span');
        const title = document.createElement('strong');
        title.textContent = player.name;
        const detail = document.createElement('small');
        const recent = `${player.recentCount} recent marker${player.recentCount === 1 ? '' : 's'}`;
        detail.textContent = Number.isFinite(player.wipeCount)
          ? `${recent} · ${player.wipeCount} this wipe`
          : recent;
        copy.append(title, detail);
        row.append(checkbox, copy);
        elements.deathFilterList.append(row);
        deathFilterCheckboxes.set(player.id, checkbox);
        checkbox.addEventListener('change', () => {
          if (checkbox.checked) activeDeathMembers.add(player.id);
          else activeDeathMembers.delete(player.id);
          deathFilterAllMode = deathFilterPlayers.size > 0
            && activeDeathMembers.size === deathFilterPlayers.size;
          applyDeathVisibility();
        });
      }
    }
    syncDeathFilterUi();
  };

  const updateTeam = (members, nowSeconds) => {
    const seen = new Set();
    for (const member of members.slice(0, 32)) {
      if (!member?.id || !member.online || !member.alive || !member.has_position) continue;
      const position = rustPlusMapToScene(manifest, member.x, member.y);
      if (!position) continue;
      position.y = terrainHeightAtScene(
        terrainChunk,
        manifest,
        position.x,
        position.z,
        reliefScale,
      ) + teamSize * 0.66;
      const id = String(member.id);
      const detail = teamIntelMemberDetail(member, nowSeconds);
      let entry = teamEntries.get(id);
      if (!entry) {
        const icon = new THREE.Sprite(teamMaterial);
        icon.scale.set(teamSize, teamSize, 1);
        icon.renderOrder = 32;
        const label = teamIntelLabelSprite(member.name, span);
        teamGroup.add(icon);
        teamGroup.add(label);
        entry = { icon, label, name: String(member.name || '') };
        teamEntries.set(id, entry);
      } else if (entry.name !== String(member.name || '')) {
        disposeDetachedSprite(entry.label);
        entry.label = teamIntelLabelSprite(member.name, span);
        teamGroup.add(entry.label);
        entry.name = String(member.name || '');
      }
      entry.icon.position.copy(position);
      entry.label.position.copy(position);
      const liveMarkerVisible = teamFilterAllMode || activeTeamMembers.has(id);
      entry.icon.visible = liveMarkerVisible;
      entry.label.visible = liveMarkerVisible;
      entry.label.position.y += teamSize * 0.82;
      setTeamIntelPickData(entry.icon, 'Live teammate', member.name, detail, id);
      setTeamIntelPickData(entry.label, 'Live teammate', member.name, detail, id);
      seen.add(id);
    }
    for (const [id, entry] of [...teamEntries.entries()]) {
      if (seen.has(id)) continue;
      removeTeamEntry(entry);
      teamEntries.delete(id);
    }
  };

  const updateDeaths = (deaths, nowSeconds) => {
    const seen = new Set();
    const candidates = recentDeathCandidates(deaths);
    for (const death of candidates) {
      const position = rustPlusMapToScene(manifest, death.x, death.y);
      if (!position) continue;
      position.y = terrainHeightAtScene(
        terrainChunk,
        manifest,
        position.x,
        position.z,
        reliefScale,
      ) + deathSize * 0.58;
      const id = String(death.id);
      const memberId = deathMemberKey(death);
      const detail = teamIntelDeathDetail(death, nowSeconds);
      let entry = deathEntries.get(id);
      if (!entry) {
        const icon = new THREE.Sprite(teamIntelMarkerMaterial('death', teamIntelDeathInitial(death.name)));
        icon.scale.set(deathSize, deathSize, 1);
        icon.renderOrder = 30;
        deathGroup.add(icon);
        entry = { icon, memberId };
        deathEntries.set(id, entry);
      }
      entry.memberId = memberId;
      entry.icon.position.copy(position);
      entry.icon.visible = deathFilterAllMode || activeDeathMembers.has(memberId);
      setTeamIntelPickData(entry.icon, 'Teammate death', death.name, detail, memberId);
      seen.add(id);
    }
    for (const [id, entry] of [...deathEntries.entries()]) {
      if (seen.has(id)) continue;
      removeDeathEntry(entry);
      deathEntries.delete(id);
    }
  };

  const syncSecurityFacingVisibility = () => {
    for (const entry of securityFacingEntries.values()) {
      const kind = String(entry?.userData?.securityKind || 'camera');
      entry.visible = kind === 'turret' ? turretGroup.visible : cameraGroup.visible;
    }
    runtime.invalidate(2);
  };

  const updateSecurityDevices = (devices, nowSeconds) => {
    const seen = new Set();
    let cameraCount = 0;
    let turretCount = 0;
    let facingCount = 0;
    for (const rawDevice of devices.slice(0, 48)) {
      const id = String(rawDevice?.id || '').trim();
      if (!id) continue;
      const kind = String(rawDevice?.kind || 'camera').toLowerCase();
      if (kind === 'turret') turretCount += 1;
      else cameraCount += 1;
      seen.add(id);

      const position = Array.isArray(rawDevice?.position) ? rawDevice.position : null;
      if (!rawDevice?.has_position || !position || position.length < 3) {
        const old = securityEntries.get(id);
        if (old) {
          removeSecurityEntry(old);
          securityEntries.delete(id);
        }
        const oldFacing = securityFacingEntries.get(id);
        if (oldFacing) {
          removeSecurityFacingEntry(oldFacing);
          securityFacingEntries.delete(id);
        }
        continue;
      }
      const scenePosition = rustWorldToScene(manifest, position[0], position[1], position[2]);
      if (!scenePosition) continue;
      const groundY = terrainHeightAtScene(
        terrainChunk,
        manifest,
        scenePosition.x,
        scenePosition.z,
        reliefScale,
      );
      scenePosition.y = groundY + securitySize * 0.62;
      const name = String(rawDevice?.name || (kind === 'turret' ? 'Auto Turret' : 'Camera')).slice(0, 80);
      const detail = securityDeviceDetail(rawDevice, manifest, nowSeconds);
      const device = { ...rawDevice, id, kind, name, _detail: detail };
      const targetGroup = kind === 'turret' ? turretGroup : cameraGroup;
      let entry = securityEntries.get(id);
      if (!entry || entry.kind !== kind || entry.name !== name) {
        if (entry) removeSecurityEntry(entry);
        const icon = new THREE.Sprite(securityMarkerMaterial(kind));
        icon.scale.set(securitySize, securitySize, 1);
        icon.renderOrder = 34;
        const label = securityLabelSprite(name, kind, span);
        targetGroup.add(icon);
        targetGroup.add(label);
        entry = { icon, label, kind, name, device };
        securityEntries.set(id, entry);
      } else if (entry.icon.parent !== targetGroup) {
        entry.icon.parent?.remove(entry.icon);
        entry.label.parent?.remove(entry.label);
        targetGroup.add(entry.icon);
        targetGroup.add(entry.label);
      }
      entry.device = device;
      entry.icon.position.copy(scenePosition);
      entry.label.position.copy(scenePosition);
      entry.label.position.y += securitySize * 0.82;
      setSecurityPickData(entry.icon, device);
      setSecurityPickData(entry.label, device);

      const oldFacing = securityFacingEntries.get(id);
      if (oldFacing) {
        removeSecurityFacingEntry(oldFacing);
        securityFacingEntries.delete(id);
      }
      if (rawDevice?.has_rotation) {
        const anchor = new THREE.Vector3(scenePosition.x, groundY + 1.2, scenePosition.z);
        const facing = securityFacingMesh(device, anchor, span);
        if (facing) {
          securityFacingGroup.add(facing);
          securityFacingEntries.set(id, facing);
          facingCount += 1;
        }
      }
    }

    for (const [id, entry] of [...securityEntries.entries()]) {
      if (seen.has(id)) continue;
      removeSecurityEntry(entry);
      securityEntries.delete(id);
    }
    for (const [id, entry] of [...securityFacingEntries.entries()]) {
      if (seen.has(id)) continue;
      removeSecurityFacingEntry(entry);
      securityFacingEntries.delete(id);
    }
    lastCameraCount = cameraCount;
    lastTurretCount = turretCount;
    elements.cameras.hidden = cameraCount === 0;
    elements.turrets.hidden = turretCount === 0;
    elements.securityFacing.hidden = facingCount === 0;
    syncSecurityFacingVisibility();
  };

  const trailSegments = (rawPoints) => {
    const segments = [];
    let current = [];
    let previous = null;
    for (const raw of rawPoints) {
      if (!Array.isArray(raw) || raw.length < 4) continue;
      const observedAt = Number(raw[0]);
      const rustX = Number(raw[1]);
      const rustY = Number(raw[2]);
      const spawnTime = Number(raw[3]) || 0;
      if (!Number.isFinite(observedAt) || !Number.isFinite(rustX) || !Number.isFinite(rustY)) continue;
      const position = rustPlusMapToScene(manifest, rustX, rustY);
      if (!position) continue;
      position.y = terrainHeightAtScene(
        terrainChunk,
        manifest,
        position.x,
        position.z,
        reliefScale,
      ) + Math.max(2.5, span * 0.0007);
      const point = { observedAt, rustX, rustY, spawnTime, position };
      const spawnChanged = previous
        && previous.spawnTime > 0
        && spawnTime > 0
        && previous.spawnTime !== spawnTime;
      const elapsed = previous ? Math.max(1, observedAt - previous.observedAt) : 1;
      const distance = previous ? Math.hypot(rustX - previous.rustX, rustY - previous.rustY) : 0;
      const impossibleJump = previous && elapsed <= 60 && distance / elapsed > 22;
      if ((spawnChanged || impossibleJump) && current.length) {
        if (current.length >= 2) segments.push(current);
        current = [];
      }
      current.push(point);
      previous = point;
    }
    if (current.length >= 2) segments.push(current);
    return segments;
  };

  const drawTrail = (trail, oldestAt, newestAt) => {
    const memberId = String(trail?.member_id || '');
    if (!memberId) return null;
    const group = new THREE.Group();
    group.name = `team-trail-${memberId}`;

    const selected = isHighlightedTrail(memberId);
    const baseColour = selected ? TRAIL_RED.clone() : TRAIL_RED_DIM.clone();
    const fade = Boolean(elements.trailFadeToggle.checked);
    const oldestColour = baseColour.clone().multiplyScalar(selected ? 0.50 : 0.34);
    const mainWidth = selected ? 5.6 : 3.8;
    const haloWidth = selected ? 8.2 : 5.8;
    const mainOpacity = selected ? 1.0 : 0.66;
    const haloOpacity = selected ? 0.50 : 0.28;
    const dashSize = Math.max(18, span * 0.0055);
    const gapSize = Math.max(12, span * 0.0036);

    for (const segment of trailSegments(Array.isArray(trail.points) ? trail.points : [])) {
      const points = segment.map((point) => point.position);
      const haloGeometry = new LineGeometry();
      haloGeometry.setFromPoints(points);
      const mainGeometry = new LineGeometry();
      mainGeometry.setFromPoints(points);

      if (fade) {
        const colours = [];
        const duration = Math.max(1, newestAt - oldestAt);
        for (const point of segment) {
          const ageRatio = THREE.MathUtils.clamp((point.observedAt - oldestAt) / duration, 0, 1);
          const colour = oldestColour.clone().lerp(baseColour, 0.25 + ageRatio * 0.75);
          colours.push(colour.r, colour.g, colour.b);
        }
        mainGeometry.setColors(colours);
      }

      const haloMaterial = new LineMaterial({
        color: TRAIL_HALO,
        linewidth: haloWidth,
        dashed: true,
        dashSize,
        gapSize,
        transparent: true,
        opacity: haloOpacity,
        depthTest: false,
        depthWrite: false,
        toneMapped: false,
      });
      const haloLine = new Line2(haloGeometry, haloMaterial);
      haloLine.computeLineDistances();
      haloLine.renderOrder = 28;
      group.add(haloLine);

      const mainMaterial = new LineMaterial({
        color: fade ? 0xffffff : baseColour,
        linewidth: mainWidth,
        dashed: true,
        dashSize,
        gapSize,
        vertexColors: fade,
        transparent: true,
        opacity: mainOpacity,
        depthTest: false,
        depthWrite: false,
        toneMapped: false,
      });
      const mainLine = new Line2(mainGeometry, mainMaterial);
      mainLine.computeLineDistances();
      mainLine.renderOrder = 29;
      group.add(mainLine);
    }
    if (group.children.length === 0) {
      removeTrailEntry({ group });
      return null;
    }
    trailGroup.add(group);
    return { group, name: String(trail?.name || 'Teammate'), colour: baseColour };
  };

  const renderTrails = (payload) => {
    clearTrailEntries();
    const trails = Array.isArray(payload?.trails) ? payload.trails : [];
    let oldestAt = Number.POSITIVE_INFINITY;
    let newestAt = Number.NEGATIVE_INFINITY;
    for (const trail of trails) {
      for (const point of (Array.isArray(trail?.points) ? trail.points : [])) {
        const observedAt = Number(point?.[0]);
        if (!Number.isFinite(observedAt)) continue;
        oldestAt = Math.min(oldestAt, observedAt);
        newestAt = Math.max(newestAt, observedAt);
      }
    }
    if (!Number.isFinite(oldestAt) || !Number.isFinite(newestAt)) {
      oldestAt = Math.floor(Date.now() / 1000);
      newestAt = oldestAt + 1;
    }
    trailRangeLabel = String(payload?.range_label || trailRangeLabel);
    let pointCount = 0;
    for (const trail of trails) {
      const memberId = String(trail?.member_id || '');
      if (!memberId) continue;
      pointCount += Array.isArray(trail?.points) ? trail.points.length : 0;
      const entry = drawTrail(trail, oldestAt, newestAt);
      if (entry) trailEntries.set(memberId, entry);
    }
    lastTrailPointCount = pointCount;
    applyTrailVisibility();
    syncTrailFilterUi();
    elements.teamIntelDetail.textContent = `${payload?.range_label || trailRangeLabel} trails · current wipe · read-only`;
    runtime.invalidate(4);
  };

  const cancelTrailTimer = () => {
    if (trailTimer !== null) window.clearTimeout(trailTimer);
    trailTimer = null;
  };

  const scheduleTrailRefresh = (seconds = 10) => {
    cancelTrailTimer();
    if (stopped || !trailGroup.visible || !trailsAvailable || runtime.isStopped()) return;
    trailTimer = window.setTimeout(() => { void refreshTrails(); }, Math.max(5, seconds) * 1000);
  };

  async function refreshTrails() {
    if (stopped || !trailGroup.visible || !trailsAvailable || runtime.isStopped()) {
      cancelTrailTimer();
      return;
    }
    const memberIds = selectedTrailPlayers().map((player) => player.id);
    if (memberIds.length === 0) {
      clearTrailEntries();
      syncTrailFilterUi();
      refreshTeamIntelSummary();
      cancelTrailTimer();
      return;
    }
    const requestVersion = ++trailRequestVersion;
    const query = new URLSearchParams({
      range: trailRangeKey,
      members: memberIds.join(','),
    });
    try {
      const payload = await requestJson(`${trailEndpoint}?${query.toString()}`, { cache: 'no-store' });
      if (requestVersion !== trailRequestVersion || stopped || runtime.isStopped()) return;
      if (!payload?.available) {
        clearTrailEntries();
        elements.trailFilterSummary.textContent = payload?.message || 'Trail history is unavailable right now.';
        scheduleTrailRefresh(15);
        return;
      }
      renderTrails(payload);
      const delay = THREE.MathUtils.clamp(
        Number(payload?.refresh_seconds) || Number(payload?.poll_seconds) * 2 || 10,
        8,
        60,
      );
      scheduleTrailRefresh(delay);
    } catch (error) {
      if (requestVersion !== trailRequestVersion || stopped || runtime.isStopped()) return;
      console.warn('Teammate trail refresh failed', redactedDiagnostic(error));
      elements.trailFilterSummary.textContent = 'Trail history could not refresh. Live teammate markers are unaffected.';
      scheduleTrailRefresh(15);
    }
  }

  const focusTrailMember = (memberId) => {
    const id = String(memberId || '').trim();
    if (!trailsAvailable || !trailFilterPlayers.has(id)) return;
    activeTrailMembers.clear();
    activeTrailMembers.add(id);
    trailFilterAllMode = false;
    highlightedTrailMemberId = id;
    trailGroup.visible = true;
    elements.trails.setAttribute('aria-pressed', 'true');
    elements.trails.textContent = 'Trails: On';
    applyTrailVisibility();
    void refreshTrails();
  };
  runtime.focusTrailMember = focusTrailMember;

  const applyPayload = (payload) => {
    if (!payload?.available) {
      clearEntries();
      elements.teamIntelSummary.textContent = payload?.message || 'Live team data is unavailable right now.';
      const reason = String(payload?.reason || '').trim();
      elements.teamIntelDetail.textContent = reason
        ? `Reason: ${reason} · the normal 3D map is still available.`
        : 'The normal 3D map is still available.';
      elements.cameras.hidden = true;
      elements.turrets.hidden = true;
      elements.securityFacing.hidden = true;
      elements.selectionSecurityViewButton.hidden = true;
      return false;
    }
    const nowSeconds = Math.floor(Date.now() / 1000);
    const members = Array.isArray(payload.members) ? payload.members : [];
    const deaths = Array.isArray(payload.deaths) ? payload.deaths : [];
    const securityDevices = Array.isArray(payload.security_devices) ? payload.security_devices : [];
    rebuildTeamFilter(members);
    rebuildDeathFilter(members, deaths);
    rebuildTrailFilter(members);
    updateTeam(members, nowSeconds);
    updateBase(payload.base);
    updateDeaths(deaths, nowSeconds);
    updateSecurityDevices(securityDevices, nowSeconds);
    lastLiveCount = members.filter((member) => member?.online && member?.alive && member?.has_position).length;
    lastOnlineCount = members.filter((member) => member?.online).length;
    refreshTeamIntelSummary();
    if (!trailGroup.visible) {
      const pairedOnline = Boolean(payload.security?.paired_player_online);
      const securityConfigured = Number(payload.security?.configured_count || 0) > 0;
      elements.teamIntelDetail.textContent = pairedOnline && securityConfigured
        ? `${payload.server?.name || 'Rust server'} · security feeds paused while the paired Rust account is in-game · last-known device positions remain visible`
        : `${payload.server?.name || 'Rust server'} · updated now · read-only`;
    }
    runtime.invalidate(3);
    return true;
  };

  const closeTeamFilterPanel = () => {
    elements.teamFilterPanel.hidden = true;
    elements.teamFilterButton.setAttribute('aria-expanded', 'false');
  };
  const toggleTeamFilterPanel = () => {
    const opening = elements.teamFilterPanel.hidden;
    elements.teamFilterPanel.hidden = !opening;
    elements.teamFilterButton.setAttribute('aria-expanded', String(opening));
    if (opening) {
      elements.monumentFilterPanel.hidden = true;
      elements.monumentFilterButton.setAttribute('aria-expanded', 'false');
      elements.deathFilterPanel.hidden = true;
      elements.deathFilterButton.setAttribute('aria-expanded', 'false');
      elements.trailFilterPanel.hidden = true;
      elements.trailFilterButton.setAttribute('aria-expanded', 'false');
    }
  };
  const closeDeathFilterPanel = () => {
    elements.deathFilterPanel.hidden = true;
    elements.deathFilterButton.setAttribute('aria-expanded', 'false');
  };
  const toggleDeathFilterPanel = () => {
    const opening = elements.deathFilterPanel.hidden;
    elements.deathFilterPanel.hidden = !opening;
    elements.deathFilterButton.setAttribute('aria-expanded', String(opening));
    if (opening) {
      elements.monumentFilterPanel.hidden = true;
      elements.monumentFilterButton.setAttribute('aria-expanded', 'false');
      elements.teamFilterPanel.hidden = true;
      elements.teamFilterButton.setAttribute('aria-expanded', 'false');
      elements.trailFilterPanel.hidden = true;
      elements.trailFilterButton.setAttribute('aria-expanded', 'false');
    }
  };
  const closeTrailFilterPanel = () => {
    elements.trailFilterPanel.hidden = true;
    elements.trailFilterButton.setAttribute('aria-expanded', 'false');
  };
  const toggleTrailFilterPanel = () => {
    const opening = elements.trailFilterPanel.hidden;
    elements.trailFilterPanel.hidden = !opening;
    elements.trailFilterButton.setAttribute('aria-expanded', String(opening));
    if (opening) {
      elements.monumentFilterPanel.hidden = true;
      elements.monumentFilterButton.setAttribute('aria-expanded', 'false');
      elements.teamFilterPanel.hidden = true;
      elements.teamFilterButton.setAttribute('aria-expanded', 'false');
      elements.deathFilterPanel.hidden = true;
      elements.deathFilterButton.setAttribute('aria-expanded', 'false');
    }
  };
  const showAllTeam = () => setAllTeamFilters(true);
  const hideAllTeam = () => setAllTeamFilters(false);
  const showAllDeaths = () => setAllDeathFilters(true);
  const hideAllDeaths = () => setAllDeathFilters(false);
  const showAllTrails = () => setAllTrailFilters(true);
  const hideAllTrails = () => setAllTrailFilters(false);
  const onTrailRangeChange = () => {
    trailRangeKey = String(elements.trailRangeSelect.value || '15m');
    const option = elements.trailRangeSelect.selectedOptions?.[0];
    trailRangeLabel = String(option?.textContent || 'Last 15 min');
    syncTrailFilterUi();
    if (trailGroup.visible) void refreshTrails();
  };
  const onTrailFadeChange = () => {
    if (trailGroup.visible) void refreshTrails();
  };
  const onTrailsToggle = () => {
    if (trailGroup.visible) {
      void refreshTrails();
    } else {
      ++trailRequestVersion;
      cancelTrailTimer();
      refreshTeamIntelSummary();
    }
  };
  const focusTeamMember = (memberId) => {
    const id = String(memberId || '').trim();
    if (!teamFilterPlayers.has(id)) return;
    activeTeamMembers.clear();
    activeTeamMembers.add(id);
    teamFilterAllMode = false;
    teamGroup.visible = true;
    elements.team.setAttribute('aria-pressed', 'true');
    elements.team.textContent = 'Team: On';
    applyTeamVisibility();
  };
  runtime.focusTeamMember = focusTeamMember;

  const onTeamToggle = () => refreshTeamIntelSummary();
  const onSelectionTeam = () => {
    const memberId = String(elements.selectionTeamButton.dataset.memberId || '');
    if (memberId) focusTeamMember(memberId);
  };
  const onSelectionTrail = () => {
    const memberId = String(elements.selectionTrailButton.dataset.memberId || '');
    if (memberId) focusTrailMember(memberId);
  };
  const onSelectionSecurityView = () => {
    const deviceId = String(elements.selectionSecurityViewButton.dataset.deviceId || '');
    const entry = securityEntries.get(deviceId);
    if (entry?.device) openSecurityViewer(entry.device);
  };
  const onSecurityLayerToggle = () => syncSecurityFacingVisibility();

  elements.team.addEventListener('click', onTeamToggle);
  elements.teamFilterButton.addEventListener('click', toggleTeamFilterPanel);
  elements.teamFilterClose.addEventListener('click', closeTeamFilterPanel);
  elements.teamFilterAll.addEventListener('click', showAllTeam);
  elements.teamFilterNone.addEventListener('click', hideAllTeam);
  elements.deathFilterButton.addEventListener('click', toggleDeathFilterPanel);
  elements.deathFilterClose.addEventListener('click', closeDeathFilterPanel);
  elements.deathFilterAll.addEventListener('click', showAllDeaths);
  elements.deathFilterNone.addEventListener('click', hideAllDeaths);
  if (trailsAvailable) {
    elements.trailFilterButton.addEventListener('click', toggleTrailFilterPanel);
    elements.trailFilterClose.addEventListener('click', closeTrailFilterPanel);
    elements.trailFilterAll.addEventListener('click', showAllTrails);
    elements.trailFilterNone.addEventListener('click', hideAllTrails);
    elements.trailRangeSelect.addEventListener('change', onTrailRangeChange);
    elements.trailFadeToggle.addEventListener('change', onTrailFadeChange);
    elements.trails.addEventListener('click', onTrailsToggle);
    elements.selectionTrailButton.addEventListener('click', onSelectionTrail);
  }
  elements.selectionTeamButton.addEventListener('click', onSelectionTeam);
  elements.selectionSecurityViewButton.addEventListener('click', onSelectionSecurityView);
  elements.securityViewerClose.addEventListener('click', closeSecurityViewer);
  elements.cameras.addEventListener('click', onSecurityLayerToggle);
  elements.turrets.addEventListener('click', onSecurityLayerToggle);
  runtime.resetTeamFilters = showAllTeam;
  runtime.resetDeathFilters = showAllDeaths;
  runtime.closeSecurityViewer = closeSecurityViewer;
  runtime.resumeSecurityViewer = () => {
    if (!activeSecurityViewerDevice || runtime.isStopped()) return;
    if (securityViewerTimer !== null) clearTimeout(securityViewerTimer);
    securityViewerTimer = null;
    void refreshSecurityFrame(securityViewerRequestVersion);
  };
  runtime.resetTrailFilters = () => {
    highlightedTrailMemberId = '';
    setAllTrailFilters(true);
    trailRangeKey = '15m';
    trailRangeLabel = 'Last 15 min';
    elements.trailRangeSelect.value = '15m';
    elements.trailFadeToggle.checked = true;
    clearTrailEntries();
  };

  const poll = async () => {
    if (stopped || activityClosed || runtime.isStopped()) return;
    let delaySeconds = 5;
    try {
      const payload = await requestJson(endpoint, { cache: 'no-store' });
      applyPayload(payload);
      delaySeconds = THREE.MathUtils.clamp(Number(payload?.poll_seconds) || 5, 3, 15);
      const permanentReasons = new Set([
        'manual_map',
        'unsupported_view',
        'server_unavailable',
        'map_changed',
        'server_identity_mismatch',
        'world_size_mismatch',
      ]);
      if (payload && payload.available === false && permanentReasons.has(payload.reason)) {
        stopped = true;
        elements.team.hidden = true;
        elements.teamFilterButton.hidden = true;
        elements.teamFilterPanel.hidden = true;
        elements.deaths.hidden = true;
        elements.deathFilterButton.hidden = true;
        elements.deathFilterPanel.hidden = true;
        elements.trails.hidden = true;
        elements.trailFilterButton.hidden = true;
        elements.trailFilterPanel.hidden = true;
        elements.trailRangePicker.hidden = true;
        elements.cameras.hidden = true;
        elements.turrets.hidden = true;
        elements.securityFacing.hidden = true;
        closeSecurityViewer();
        cancelTrailTimer();
        return;
      }
    } catch (error) {
      console.warn('Live Team Intel refresh failed', redactedDiagnostic(error));
      clearEntries();
      elements.teamIntelSummary.textContent = 'Live team data could not refresh.';
      elements.teamIntelDetail.textContent = 'The normal 3D map is unaffected.';
      if (['team_intel_forbidden', 'world_expired', 'world_not_found'].includes(String(error?.code || ''))) {
        stopped = true;
        elements.team.hidden = true;
        elements.teamFilterButton.hidden = true;
        elements.teamFilterPanel.hidden = true;
        elements.deaths.hidden = true;
        elements.deathFilterButton.hidden = true;
        elements.deathFilterPanel.hidden = true;
        elements.trails.hidden = true;
        elements.trailFilterButton.hidden = true;
        elements.trailFilterPanel.hidden = true;
        elements.trailRangePicker.hidden = true;
        elements.cameras.hidden = true;
        elements.turrets.hidden = true;
        elements.securityFacing.hidden = true;
        elements.teamIntelPanel.hidden = true;
        closeSecurityViewer();
        cancelTrailTimer();
        return;
      }
    }
    if (!stopped && !activityClosed && !runtime.isStopped()) {
      timer = window.setTimeout(poll, delaySeconds * 1000);
    }
  };

  runtime.addCleanup(() => {
    stopped = true;
    ++trailRequestVersion;
    if (timer !== null) window.clearTimeout(timer);
    cancelTrailTimer();
    clearEntries();
    teamMaterial.map?.dispose?.();
    teamMaterial.dispose?.();
    elements.team.removeEventListener('click', onTeamToggle);
    elements.teamFilterButton.removeEventListener('click', toggleTeamFilterPanel);
    elements.teamFilterClose.removeEventListener('click', closeTeamFilterPanel);
    elements.teamFilterAll.removeEventListener('click', showAllTeam);
    elements.teamFilterNone.removeEventListener('click', hideAllTeam);
    elements.deathFilterButton.removeEventListener('click', toggleDeathFilterPanel);
    elements.deathFilterClose.removeEventListener('click', closeDeathFilterPanel);
    elements.deathFilterAll.removeEventListener('click', showAllDeaths);
    elements.deathFilterNone.removeEventListener('click', hideAllDeaths);
    if (trailsAvailable) {
      elements.trailFilterButton.removeEventListener('click', toggleTrailFilterPanel);
      elements.trailFilterClose.removeEventListener('click', closeTrailFilterPanel);
      elements.trailFilterAll.removeEventListener('click', showAllTrails);
      elements.trailFilterNone.removeEventListener('click', hideAllTrails);
      elements.trailRangeSelect.removeEventListener('change', onTrailRangeChange);
      elements.trailFadeToggle.removeEventListener('change', onTrailFadeChange);
      elements.trails.removeEventListener('click', onTrailsToggle);
      elements.selectionTrailButton.removeEventListener('click', onSelectionTrail);
    }
    elements.selectionTeamButton.removeEventListener('click', onSelectionTeam);
    elements.selectionSecurityViewButton.removeEventListener('click', onSelectionSecurityView);
    elements.securityViewerClose.removeEventListener('click', closeSecurityViewer);
    elements.cameras.removeEventListener('click', onSecurityLayerToggle);
    elements.turrets.removeEventListener('click', onSecurityLayerToggle);
    closeSecurityViewer();
    elements.team.hidden = true;
    elements.teamFilterButton.hidden = true;
    elements.teamFilterPanel.hidden = true;
    elements.teamFilterButton.setAttribute('aria-expanded', 'false');
    elements.teamFilterList.replaceChildren();
    elements.deaths.hidden = true;
    elements.deathFilterButton.hidden = true;
    elements.deathFilterPanel.hidden = true;
    elements.deathFilterButton.setAttribute('aria-expanded', 'false');
    elements.deathFilterList.replaceChildren();
    elements.trails.hidden = true;
    elements.trailFilterButton.hidden = true;
    elements.trailFilterPanel.hidden = true;
    elements.trailRangePicker.hidden = true;
    elements.trailFilterButton.setAttribute('aria-expanded', 'false');
    elements.trailFilterList.replaceChildren();
    elements.cameras.hidden = true;
    elements.turrets.hidden = true;
    elements.securityFacing.hidden = true;
    highlightedTrailMemberId = '';
    runtime.highlightTrailMember = null;
    elements.selectionTeamButton.hidden = true;
    elements.selectionTeamButton.dataset.memberId = '';
    elements.selectionTrailButton.hidden = true;
    elements.selectionTrailButton.dataset.memberId = '';
    elements.selectionSecurityViewButton.hidden = true;
    elements.selectionSecurityViewButton.dataset.deviceId = '';
    elements.teamIntelPanel.hidden = true;
    runtime.resetTeamFilters = null;
    runtime.resetDeathFilters = null;
    runtime.closeSecurityViewer = null;
    runtime.resumeSecurityViewer = null;
    runtime.securityFrameAvailable = false;
    runtime.resetTrailFilters = null;
    runtime.focusTeamMember = null;
    runtime.focusTrailMember = null;
  });
  void poll();
}
function addWorldMarkers(runtime, manifest, terrainChunk, span, reliefScale, groups) {
  const red = markerMaterial('#e94c3b');
  const blue = markerMaterial('#159fc3');
  const neutral = markerMaterial('#263c3d');
  const size = Math.max(28, Math.min(82, span * 0.017));
  let count = 0;
  for (const marker of (manifest.markers || []).slice(0, 240)) {
    if (!Array.isArray(marker.position) || marker.position.length !== 3) continue;
    const name = String(marker.name || '').toLowerCase();
    const material = name.includes('fishing') || name.includes('outpost')
      ? blue
      : name.includes('cave') || name.includes('warehouse')
        ? neutral
        : red;
    const icon = new THREE.Sprite(material);
    icon.position.fromArray(marker.position);
    icon.position.y = terrainHeightAtScene(
      terrainChunk,
      manifest,
      icon.position.x,
      icon.position.z,
      reliefScale,
    ) + size * 0.72;
    icon.scale.set(size, size, 1);
    icon.renderOrder = 20;
    icon.userData.monument = marker.name;
    icon.userData.monumentCategory = String(marker.category || 'industrial');
    groups.icons.add(icon);
    const label = labelSprite(marker.name, span);
    label.position.copy(icon.position);
    label.position.y += size * 0.72;
    label.userData.monument = marker.name;
    label.userData.monumentCategory = String(marker.category || 'industrial');
    groups.labels.add(label);
    count += 1;
  }
  if (count === 0) {
    for (const material of [red, blue, neutral]) {
      material.map?.dispose();
      material.dispose();
    }
  }
  return count;
}

function gridColumnLabel(index) {
  let value = Math.max(0, Math.floor(index));
  let label = '';
  do {
    label = String.fromCharCode(65 + (value % 26)) + label;
    value = Math.floor(value / 26) - 1;
  } while (value >= 0);
  return label;
}

function gridLabelSprite(text, span) {
  const canvas = document.createElement('canvas');
  canvas.width = 128;
  canvas.height = 64;
  const context = canvas.getContext('2d');
  context.fillStyle = 'rgba(4, 25, 29, 0.88)';
  context.beginPath();
  context.roundRect(20, 7, 88, 50, 10);
  context.fill();
  context.fillStyle = '#f4f1da';
  context.font = '800 30px Inter, system-ui, sans-serif';
  context.textAlign = 'center';
  context.textBaseline = 'middle';
  context.fillText(String(text), 64, 33, 76);
  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  const sprite = new THREE.Sprite(new THREE.SpriteMaterial({
    map: texture,
    transparent: true,
    depthTest: false,
    depthWrite: false,
  }));
  const width = Math.max(34, Math.min(62, span * 0.013));
  sprite.scale.set(width, width * 0.5, 1);
  sprite.renderOrder = 18;
  return sprite;
}

function addWorldGrid(manifest, terrainChunk, reliefScale, group) {
  const worldSize = Number(manifest.world_size || 0);
  const cellSize = Number(manifest.grid?.cell_size_metres || 150);
  if (!(worldSize > 0) || !(cellSize > 0)) return 0;
  const half = worldSize / 2;
  const origin = manifest.coordinate_system.scene_origin_rust;
  const segments = Math.max(24, Math.min(80, Math.ceil(worldSize / 60)));
  const boundaries = Math.ceil(worldSize / cellSize);
  const material = new THREE.LineBasicMaterial({
    color: 0x10282e,
    transparent: true,
    opacity: 0.48,
    depthWrite: false,
  });
  const makeLine = (points) => {
    const geometry = new THREE.BufferGeometry().setFromPoints(points);
    const line = new THREE.Line(geometry, material);
    line.renderOrder = 12;
    group.add(line);
  };
  for (let index = 0; index <= boundaries; index += 1) {
    const worldX = Math.min(half, -half + index * cellSize);
    const x = worldX - origin[0];
    const vertical = [];
    for (let sample = 0; sample <= segments; sample += 1) {
      const worldZ = -half + (sample / segments) * worldSize;
      const z = -(worldZ - origin[2]);
      vertical.push(new THREE.Vector3(
        x,
        terrainHeightAtScene(terrainChunk, manifest, x, z, reliefScale) + 0.9,
        z,
      ));
    }
    makeLine(vertical);

    const worldZ = Math.max(-half, half - index * cellSize);
    const z = -(worldZ - origin[2]);
    const horizontal = [];
    for (let sample = 0; sample <= segments; sample += 1) {
      const sampleWorldX = -half + (sample / segments) * worldSize;
      const sampleX = sampleWorldX - origin[0];
      horizontal.push(new THREE.Vector3(
        sampleX,
        terrainHeightAtScene(terrainChunk, manifest, sampleX, z, reliefScale) + 0.9,
        z,
      ));
    }
    makeLine(horizontal);
  }
  for (let index = 0; index < boundaries; index += 1) {
    if (boundaries > 32 && index % 2 !== 0) continue;
    const centreWorldX = Math.min(half, -half + (index + 0.5) * cellSize);
    const centreWorldZ = Math.max(-half, half - (index + 0.5) * cellSize);
    const northX = centreWorldX - origin[0];
    const northZ = -(half - origin[2]);
    const column = gridLabelSprite(gridColumnLabel(index), worldSize);
    column.position.set(
      northX,
      terrainHeightAtScene(terrainChunk, manifest, northX, northZ, reliefScale) + 15,
      northZ,
    );
    group.add(column);
    const westX = -half - origin[0];
    const westZ = -(centreWorldZ - origin[2]);
    const row = gridLabelSprite(index, worldSize);
    row.position.set(
      westX,
      terrainHeightAtScene(terrainChunk, manifest, westX, westZ, reliefScale) + 15,
      westZ,
    );
    group.add(row);
  }
  group.userData.gridColumns = Array.from({ length: boundaries }, (_, index) => gridColumnLabel(index));
  return boundaries;
}

async function loadGltf(url, progress) {
  const draco = new DRACOLoader();
  draco.setDecoderPath(apiUrl(__MEG_DRACO_DECODER_PATH__));
  draco.setDecoderConfig({ type: 'wasm' });
  draco.preload();
  const loader = new GLTFLoader();
  loader.setDRACOLoader(draco);
  loader.setMeshoptDecoder(MeshoptDecoder);
  try {
    return await new Promise((resolve, reject) => {
      loader.load(apiUrl(url), resolve, progress, reject);
    });
  } finally {
    draco.dispose();
  }
}

function configureMonumentFilters(runtime, manifest, groups) {
  const source = Array.isArray(manifest.monument_categories) ? manifest.monument_categories : [];
  const categories = source
    .filter((entry) => /^[a-z][a-z0-9_]{0,31}$/.test(String(entry?.key || '')))
    .map((entry) => ({
      key: String(entry.key),
      label: String(entry.label || entry.key).slice(0, 60),
      count: Math.max(0, Number(entry.count || 0)),
      modelCount: Math.max(0, Number(entry.model_count || 0)),
    }));
  const active = new Set(categories.map((entry) => entry.key));
  const subscribers = new Set();
  const checkboxes = new Map();
  const isEnabled = (category) => active.has(String(category || 'industrial'));
  const applyVisibility = () => {
    for (const group of [groups.icons, groups.labels]) {
      for (const child of group.children) {
        child.visible = isEnabled(child.userData.monumentCategory);
      }
    }
    elements.monumentFilterSummary.textContent = active.size === categories.length
      ? `Showing all ${categories.length} monument types.`
      : `Showing ${active.size} of ${categories.length} monument types.`;
    for (const [key, checkbox] of checkboxes) checkbox.checked = active.has(key);
    for (const callback of subscribers) callback();
    runtime.invalidate(3);
  };
  const setAll = (visible) => {
    active.clear();
    if (visible) for (const category of categories) active.add(category.key);
    applyVisibility();
  };

  elements.monumentFilterList.replaceChildren();
  for (const category of categories) {
    const row = document.createElement('label');
    row.className = 'monument-filter-option';
    const checkbox = document.createElement('input');
    checkbox.type = 'checkbox';
    checkbox.checked = true;
    checkbox.value = category.key;
    const copy = document.createElement('span');
    const title = document.createElement('strong');
    title.textContent = category.label;
    const detail = document.createElement('small');
    detail.textContent = `${category.count} markers · ${category.modelCount} models`;
    copy.append(title, detail);
    row.append(checkbox, copy);
    elements.monumentFilterList.append(row);
    checkboxes.set(category.key, checkbox);
    const onChange = () => {
      if (checkbox.checked) active.add(category.key);
      else active.delete(category.key);
      applyVisibility();
    };
    checkbox.addEventListener('change', onChange);
    runtime.addCleanup(() => checkbox.removeEventListener('change', onChange));
  }

  const closePanel = () => {
    elements.monumentFilterPanel.hidden = true;
    elements.monumentFilterButton.setAttribute('aria-expanded', 'false');
  };
  const togglePanel = () => {
    const opening = elements.monumentFilterPanel.hidden;
    elements.monumentFilterPanel.hidden = !opening;
    elements.monumentFilterButton.setAttribute('aria-expanded', String(opening));
    if (opening) {
      elements.teamFilterPanel.hidden = true;
      elements.teamFilterButton.setAttribute('aria-expanded', 'false');
      elements.deathFilterPanel.hidden = true;
      elements.deathFilterButton.setAttribute('aria-expanded', 'false');
      elements.trailFilterPanel.hidden = true;
      elements.trailFilterButton.setAttribute('aria-expanded', 'false');
    }
  };
  const showAll = () => setAll(true);
  const hideAll = () => setAll(false);
  elements.monumentFilterButton.hidden = categories.length === 0;
  elements.monumentFilterButton.addEventListener('click', togglePanel);
  elements.monumentFilterClose.addEventListener('click', closePanel);
  elements.monumentFilterAll.addEventListener('click', showAll);
  elements.monumentFilterNone.addEventListener('click', hideAll);
  runtime.resetMonumentFilters = showAll;
  runtime.addCleanup(() => {
    elements.monumentFilterButton.removeEventListener('click', togglePanel);
    elements.monumentFilterClose.removeEventListener('click', closePanel);
    elements.monumentFilterAll.removeEventListener('click', showAll);
    elements.monumentFilterNone.removeEventListener('click', hideAll);
    elements.monumentFilterButton.hidden = true;
    elements.monumentFilterPanel.hidden = true;
    elements.monumentFilterButton.setAttribute('aria-expanded', 'false');
    elements.monumentFilterList.replaceChildren();
    subscribers.clear();
  });
  applyVisibility();
  return {
    isEnabled,
    subscribe(callback) {
      subscribers.add(callback);
      return () => subscribers.delete(callback);
    },
  };
}

function streamWorldModels(runtime, manifest, reliefScale, group, filters, onProgress = null) {
  const assetSlots = Math.max(1, Math.min(24, Number(manifest.model_coverage?.client_asset_slots || 24)));
  const instanceSlots = Math.max(1, Math.min(128, Number(manifest.model_coverage?.client_instance_slots || 128)));
  const placements = (manifest.placements || []).filter((placement) => (
    placement?.asset_id && Array.isArray(placement.scene_position)
  ));
  const assets = [...(manifest.assets || [])]
    .filter((asset) => asset?.url && asset.asset_id)
    .sort((a, b) => Number(a.load_priority || 0) - Number(b.load_priority || 0));
  const placementsByAsset = new Map();
  for (const placement of placements) {
    if (!placementsByAsset.has(placement.asset_id)) placementsByAsset.set(placement.asset_id, []);
    placementsByAsset.get(placement.asset_id).push(placement);
  }

  const loaded = new Map();
  let stopped = false;
  let running = false;
  let refreshRequested = false;
  let revision = 0;
  let timer = null;

  const distanceSquared = (placement, target) => {
    const position = placement.scene_position || [0, 0, 0];
    const dx = Number(position[0] || 0) - target.x;
    const dz = Number(position[2] || 0) - target.z;
    return dx * dx + dz * dz;
  };
  const activeInstances = () => [...loaded.values()]
    .reduce((total, entry) => total + entry.instances, 0);
  let desiredInstanceCount = 0;
  const report = () => onProgress?.(
    activeInstances(),
    loaded.size,
    Number(manifest.model_coverage?.included_assets || assets.length),
    group.visible,
    desiredInstanceCount,
  );
  const release = (assetId) => {
    const entry = loaded.get(assetId);
    if (!entry) return;
    loaded.delete(assetId);
    group.remove(entry.group);
    disposeSceneResources(entry.group);
  };
  const releaseAll = () => {
    for (const assetId of [...loaded.keys()]) release(assetId);
    runtime.invalidate(2);
    report();
  };
  const desiredPlans = () => {
    if (!group.visible) return [];
    const target = runtime.controls.target;
    const worldSize = Math.max(200, Number(manifest.world_size || 4000));
    const cameraDistance = runtime.camera.position.distanceTo(target);
    // At street/detail zoom every verified placement around the point being
    // viewed is eligible. At whole-map zoom, distant sub-pixel GLBs remain
    // markers until the user moves closer so Discord is not asked to decode
    // hundreds of megabytes of geometry at once.
    const detailRadius = THREE.MathUtils.clamp(
      cameraDistance * 0.68,
      Math.max(480, worldSize * 0.11),
      worldSize * 0.42,
    );
    const candidates = placements
      .filter((placement) => filters.isEnabled(placement.category))
      .map((placement) => ({ placement, distance: distanceSquared(placement, target) }))
      .filter((candidate) => candidate.distance <= detailRadius * detailRadius)
      .sort((a, b) => (
        a.distance - b.distance
        || Number(a.placement.lod?.load_priority || 0) - Number(b.placement.lod?.load_priority || 0)
      ));
    const selected = [];
    const selectedAssetIds = new Set();
    for (const candidate of candidates) {
      const assetId = candidate.placement.asset_id;
      if (!selectedAssetIds.has(assetId) && selectedAssetIds.size >= assetSlots) continue;
      selectedAssetIds.add(assetId);
      selected.push(candidate);
      if (selected.length >= instanceSlots) break;
    }
    const assetById = new Map(assets.map((asset) => [asset.asset_id, asset]));
    const plans = new Map();
    for (const candidate of selected) {
      const asset = assetById.get(candidate.placement.asset_id);
      if (!asset) continue;
      if (!plans.has(asset.asset_id)) {
        plans.set(asset.asset_id, { asset, placements: [], distance: candidate.distance });
      }
      plans.get(asset.asset_id).placements.push(candidate.placement);
    }
    return [...plans.values()].sort((a, b) => (
      a.distance - b.distance
      || Number(a.asset.load_priority || 0) - Number(b.asset.load_priority || 0)
    ));
  };

  const configureRoot = (root, placement) => {
    root.position.fromArray(placement.scene_position || [0, 0, 0]);
    root.position.y *= reliefScale;
    root.quaternion.fromArray(placement.scene_quaternion || [0, 0, 0, 1]);
    const correction = placement.correction_rotation_degrees || [0, 0, 0];
    root.quaternion.multiply(new THREE.Quaternion().setFromEuler(new THREE.Euler(
      THREE.MathUtils.degToRad(Number(correction[0] || 0)),
      THREE.MathUtils.degToRad(Number(correction[1] || 0)),
      THREE.MathUtils.degToRad(Number(correction[2] || 0)),
      'XYZ',
    )));
    root.scale.fromArray(placement.scale || [1, 1, 1]);
    root.userData.monument = placement.name;
    root.userData.monumentCategory = String(placement.category || 'industrial');
    root.traverse((object) => {
      if (object.isLight) object.visible = false;
      if (!object.isMesh) return;
      object.castShadow = false;
      object.receiveShadow = false;
      const materials = Array.isArray(object.material) ? object.material : [object.material];
      for (const material of materials) {
        if (!material) continue;
        material.envMapIntensity = 0.38;
        for (const key of ['map', 'normalMap', 'roughnessMap', 'metalnessMap', 'aoMap']) {
          const texture = material[key];
          if (texture) texture.anisotropy = Math.min(4, runtime.renderer.capabilities.getMaxAnisotropy());
        }
      }
    });
  };

  const refreshOnce = async () => {
    const refreshRevision = revision;
    const desired = desiredPlans();
    desiredInstanceCount = desired.reduce((total, plan) => total + plan.placements.length, 0);
    const desiredIds = new Set(desired.map((plan) => plan.asset.asset_id));
    for (const assetId of [...loaded.keys()]) {
      const plan = desired.find((candidate) => candidate.asset.asset_id === assetId);
      const signature = plan?.placements.map((placement) => placement.object_id).join('|') || '';
      if (!desiredIds.has(assetId) || loaded.get(assetId)?.signature !== signature) release(assetId);
    }
    report();
    for (const plan of desired) {
      if (stopped || activityClosed || runtime.isStopped() || refreshRevision !== revision) return;
      const asset = plan.asset;
      if (loaded.has(asset.asset_id)) continue;
      const assetPlacements = plan.placements;
      if (!assetPlacements.length) continue;
      try {
        const gltf = await loadGltf(asset.url, () => {});
        if (
          stopped || activityClosed || runtime.isStopped()
          || refreshRevision !== revision || !group.visible
        ) {
          disposeSceneResources(gltf.scene);
          return;
        }
        const assetGroup = new THREE.Group();
        assetGroup.name = `map-model-${asset.asset_id}`;
        for (let index = 0; index < assetPlacements.length; index += 1) {
          const root = index === 0 ? gltf.scene : gltf.scene.clone(true);
          configureRoot(root, assetPlacements[index]);
          assetGroup.add(root);
        }
        group.add(assetGroup);
        loaded.set(asset.asset_id, {
          group: assetGroup,
          instances: assetPlacements.length,
          signature: assetPlacements.map((placement) => placement.object_id).join('|'),
        });
        runtime.invalidate(3);
        report();
      } catch (error) {
        if (activityClosed || runtime.isStopped()) return;
        console.warn('Optional verified monument model was skipped', redactedDiagnostic(error));
      }
    }
  };

  const drain = async () => {
    if (running || stopped) return;
    running = true;
    try {
      while (refreshRequested && !stopped) {
        refreshRequested = false;
        await refreshOnce();
      }
    } finally {
      running = false;
    }
  };
  const schedule = (delay = 280) => {
    if (stopped) return;
    if (timer !== null) clearTimeout(timer);
    timer = setTimeout(() => {
      timer = null;
      revision += 1;
      refreshRequested = true;
      void drain();
    }, delay);
  };
  const onControlsEnd = () => schedule();
  const onModelsToggle = () => schedule(0);
  const unsubscribeFilters = filters.subscribe(() => schedule(0));
  runtime.controls.addEventListener('end', onControlsEnd);
  elements.models.addEventListener('click', onModelsToggle);
  runtime.addCleanup(() => {
    stopped = true;
    revision += 1;
    if (timer !== null) clearTimeout(timer);
    runtime.controls.removeEventListener('end', onControlsEnd);
    elements.models.removeEventListener('click', onModelsToggle);
    unsubscribeFilters();
  });
  schedule(0);
  return { releaseAll, loadedInstances: activeInstances };
}

async function loadSurfaceTexture(runtime, url) {
  if (!url) return null;
  const loader = new THREE.TextureLoader();
  const texture = await loader.loadAsync(apiUrl(url));
  if (activityClosed) {
    texture.dispose();
    return null;
  }
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.anisotropy = Math.min(8, runtime.renderer.capabilities.getMaxAnisotropy());
  texture.wrapS = THREE.ClampToEdgeWrapping;
  texture.wrapT = THREE.ClampToEdgeWrapping;
  texture.needsUpdate = true;
  return texture;
}

async function configureOverlaySelector(runtime, manifest, group, terrainMesh) {
  const sourceEntries = Array.isArray(manifest.overlays) && manifest.overlays.length
    ? manifest.overlays
    : manifest.overlay?.texture?.url
      ? [{ ...manifest.overlay, key: manifest.selected_overlay }]
      : [];
  const entries = sourceEntries.filter((entry) => (
    /^[a-z][a-z0-9_-]{0,31}$/.test(String(entry?.key || ''))
    && typeof entry?.texture?.url === 'string'
  ));
  const byKey = new Map(entries.map((entry) => [entry.key, entry]));
  elements.overlaySelect.replaceChildren();
  const none = document.createElement('option');
  none.value = 'map';
  none.textContent = 'None';
  elements.overlaySelect.append(none);
  const optionGroups = new Map();
  for (const entry of entries) {
    const groupName = String(entry.group || 'Analysis').slice(0, 40);
    if (!optionGroups.has(groupName)) {
      const optionGroup = document.createElement('optgroup');
      optionGroup.label = groupName;
      optionGroups.set(groupName, optionGroup);
      elements.overlaySelect.append(optionGroup);
    }
    const option = document.createElement('option');
    option.value = entry.key;
    option.textContent = String(entry.select_label || entry.label || entry.key).slice(0, 80);
    optionGroups.get(groupName).append(option);
  }
  elements.overlayPicker.hidden = entries.length === 0;
  elements.overlay.hidden = true;
  group.visible = false;
  if (!entries.length) return false;

  const defaultKey = byKey.has(manifest.selected_overlay) ? manifest.selected_overlay : 'map';
  elements.overlaySelect.value = defaultKey;
  const material = new THREE.MeshBasicMaterial({
    transparent: true,
    depthWrite: false,
    polygonOffset: true,
    polygonOffsetFactor: -2,
    polygonOffsetUnits: -2,
  });
  const mesh = new THREE.Mesh(terrainMesh.geometry, material);
  mesh.position.y = 0.35;
  mesh.renderOrder = 8;
  group.add(mesh);
  const textures = new Map();
  let activation = 0;

  const showOverlayInfo = (entry) => {
    if (!entry) {
      elements.overlayInfo.hidden = true;
      return;
    }
    elements.overlayInfoGroup.textContent = String(entry.group || 'Map analysis');
    elements.overlayInfoTitle.textContent = String(entry.label || entry.select_label || 'Overlay');
    elements.overlayInfoEvidence.textContent = String(entry.evidence || 'SUITABILITY').replaceAll('_', ' ');
    elements.overlayInfoDescription.textContent = String(entry.description || '');
    const low = Array.isArray(entry.colors?.low) ? entry.colors.low : [56, 194, 156];
    const high = Array.isArray(entry.colors?.high) ? entry.colors.high : [241, 77, 51];
    elements.overlayInfoGradient.style.background = `linear-gradient(90deg, rgb(${low.join(',')}), rgb(${high.join(',')}))`;
    elements.overlayInfo.hidden = false;
  };

  const activate = async (requestedKey, announce = true) => {
    const requestId = ++activation;
    const entry = byKey.get(requestedKey);
    if (!entry) {
      material.map = null;
      material.needsUpdate = true;
      group.visible = false;
      elements.overlay.hidden = true;
      showOverlayInfo(null);
      elements.overlaySelect.value = 'map';
      runtime.invalidate(3);
      if (announce) setConnected('Interactive full map');
      return;
    }
    elements.overlaySelect.disabled = true;
    if (announce) setConnected(`Loading ${entry.select_label || entry.label || 'overlay'}…`);
    try {
      let texture = textures.get(entry.key);
      if (!texture) {
        texture = await loadSurfaceTexture(runtime, entry.texture.url);
        if (runtime.isStopped()) {
          texture?.dispose();
          return;
        }
        textures.set(entry.key, texture);
      }
      if (requestId !== activation || runtime.isStopped()) return;
      material.map = texture;
      material.needsUpdate = true;
      group.visible = true;
      elements.overlay.hidden = false;
      elements.overlay.setAttribute('aria-pressed', 'true');
      elements.overlay.textContent = 'Overlay: On';
      showOverlayInfo(entry);
      runtime.invalidate(4);
      if (announce) setConnected(String(entry.label || entry.select_label || 'Overlay'));
    } catch (error) {
      if (requestId !== activation || runtime.isStopped()) return;
      console.warn('Grounded map overlay unavailable', redactedDiagnostic(error));
      material.map = null;
      material.needsUpdate = true;
      group.visible = false;
      elements.overlay.hidden = true;
      showOverlayInfo(null);
      elements.overlaySelect.value = 'map';
      if (announce) setConnected('Interactive full map');
    } finally {
      if (requestId === activation) elements.overlaySelect.disabled = false;
    }
  };
  const onSelection = () => { void activate(elements.overlaySelect.value); };
  elements.overlaySelect.addEventListener('change', onSelection);
  runtime.resetMapOverlay = () => {
    elements.overlaySelect.value = defaultKey;
    void activate(defaultKey, false);
  };
  runtime.addCleanup(() => {
    activation += 1;
    elements.overlaySelect.removeEventListener('change', onSelection);
    elements.overlayPicker.hidden = true;
    elements.overlay.hidden = true;
    elements.overlayInfo.hidden = true;
    elements.overlaySelect.replaceChildren();
    material.map = null;
    for (const texture of textures.values()) texture.dispose();
    textures.clear();
  });
  await activate(defaultKey, false);
  return true;
}

function configureMapControls(runtime, groups, span, defaultFocus, terrainMesh, manifest, reliefScale) {
  elements.mapControls.hidden = false;
  const defaults = new Map(groups.map(([button, group]) => [button, group.visible]));
  const setButton = (button, visible, label) => {
    button.setAttribute('aria-pressed', String(visible));
    button.textContent = `${label}: ${visible ? 'On' : 'Off'}`;
  };
  for (const [button, group, label] of groups) {
    setButton(button, group.visible, label);
    const toggle = () => {
      group.visible = !group.visible;
      setButton(button, group.visible, label);
      if (button === elements.overlay) elements.overlayInfo.hidden = !group.visible;
      runtime.invalidate(2);
    };
    button.addEventListener('click', toggle);
    runtime.addCleanup(() => button.removeEventListener('click', toggle));
  }

  const threeDimensionalPosition = runtime.camera.position.clone();
  const threeDimensionalTarget = defaultFocus.clone();
  let topDown = false;
  let savedPosition = threeDimensionalPosition.clone();
  let savedTarget = threeDimensionalTarget.clone();
  const applyThreeDimensional = (position = savedPosition, target = savedTarget) => {
    topDown = false;
    runtime.controls.enableRotate = true;
    runtime.camera.position.copy(position);
    runtime.controls.target.copy(target);
    elements.viewMode.textContent = '2D View';
    runtime.controls.update();
    runtime.invalidate(5);
  };
  const applyTopDown = () => {
    if (!topDown) {
      savedPosition = runtime.camera.position.clone();
      savedTarget = runtime.controls.target.clone();
    }
    topDown = true;
    runtime.controls.autoRotate = false;
    elements.rotate.setAttribute('aria-pressed', 'false');
    elements.rotate.lastChild.textContent = ' Auto Rotate: Off';
    runtime.controls.enableRotate = false;
    runtime.camera.position.set(defaultFocus.x, span * 1.22, defaultFocus.z + 0.01);
    runtime.controls.target.copy(defaultFocus);
    elements.viewMode.textContent = '3D View';
    runtime.controls.update();
    runtime.invalidate(5);
  };
  const toggleView = () => (topDown ? applyThreeDimensional() : applyTopDown());
  const recenter = () => {
    if (topDown) applyTopDown();
    else applyThreeDimensional(threeDimensionalPosition, threeDimensionalTarget);
  };
  elements.viewMode.addEventListener('click', toggleView);
  elements.recenter.addEventListener('click', recenter);
  runtime.addCleanup(() => elements.viewMode.removeEventListener('click', toggleView));
  runtime.addCleanup(() => elements.recenter.removeEventListener('click', recenter));

  runtime.resetView = () => {
    const damping = runtime.controls.enableDamping;
    const autoRotate = runtime.controls.autoRotate;
    runtime.controls.enableDamping = false;
    runtime.controls.autoRotate = false;
    // OrbitControls keeps damped deltas outside camera/target state. Resetting
    // twice clears those deltas before restoring the canonical map camera.
    runtime.controls.reset();
    runtime.controls.reset();
    for (const [button, group, label] of groups) {
      group.visible = defaults.get(button);
      setButton(button, group.visible, label);
    }
    runtime.resetMapOverlay?.();
    runtime.resetMonumentFilters?.();
    runtime.resetTeamFilters?.();
    runtime.resetDeathFilters?.();
    runtime.resetTrailFilters?.();
    runtime.closeSecurityViewer?.();
    elements.selectionPanel.hidden = true;
    elements.selectionDetail.hidden = true;
    elements.selectionTeamButton.hidden = true;
    elements.selectionTeamButton.dataset.memberId = '';
    elements.selectionTrailButton.hidden = true;
    elements.selectionTrailButton.dataset.memberId = '';
    elements.selectionSecurityViewButton.hidden = true;
    elements.selectionSecurityViewButton.dataset.deviceId = '';
    applyThreeDimensional(threeDimensionalPosition, threeDimensionalTarget);
    runtime.controls.autoRotate = autoRotate;
    runtime.controls.enableDamping = damping;
    runtime.controls.update();
  };

  const iconGroup = groups.find(([button]) => button === elements.icons)?.[1];
  const teamGroup = groups.find(([button]) => button === elements.team)?.[1];
  const deathGroup = groups.find(([button]) => button === elements.deaths)?.[1];
  const cameraGroup = groups.find(([button]) => button === elements.cameras)?.[1];
  const turretGroup = groups.find(([button]) => button === elements.turrets)?.[1];
  const raycaster = new THREE.Raycaster();
  const pointer = new THREE.Vector2();
  let pointerStart = null;
  const onPointerDown = (event) => {
    pointerStart = [event.clientX, event.clientY];
  };
  const onPointerUp = (event) => {
    if (!pointerStart || Math.hypot(event.clientX - pointerStart[0], event.clientY - pointerStart[1]) > 6) {
      pointerStart = null;
      return;
    }
    pointerStart = null;
    const rect = elements.canvas.getBoundingClientRect();
    pointer.x = ((event.clientX - rect.left) / Math.max(1, rect.width)) * 2 - 1;
    pointer.y = -((event.clientY - rect.top) / Math.max(1, rect.height)) * 2 + 1;
    raycaster.setFromCamera(pointer, runtime.camera);
    const pickableObjects = [iconGroup, teamGroup, deathGroup, cameraGroup, turretGroup]
      .filter((group) => group?.visible)
      .flatMap((group) => group.children);
    const hit = pickableObjects.length
      ? raycaster.intersectObjects(pickableObjects, false)[0]
      : null;
    if (debugRequested) {
      const terrainHit = raycaster.intersectObject(terrainMesh, false)[0];
      if (terrainHit) {
        const origin = manifest.coordinate_system.scene_origin_rust;
        const rustX = terrainHit.point.x + origin[0];
        const rustY = terrainHit.point.y / reliefScale + origin[1];
        const rustZ = origin[2] - terrainHit.point.z;
        const cellSize = Number(manifest.grid?.cell_size_metres || 150);
        const cells = Math.max(1, Math.ceil(Number(manifest.world_size) / cellSize));
        const column = THREE.MathUtils.clamp(
          Math.floor((rustX + Number(manifest.world_size) / 2) / cellSize),
          0,
          cells - 1,
        );
        const row = THREE.MathUtils.clamp(
          Math.floor((Number(manifest.world_size) / 2 - rustZ) / cellSize),
          0,
          cells - 1,
        );
        runtime.cursorWorld = {
          rust: [rustX, rustY, rustZ],
          grid: `${gridColumnLabel(column)}${row}`,
        };
      }
    }
    if (hit?.object?.userData?.securityDevice) {
      const deviceId = String(hit.object.userData.securityDeviceId || '');
      const deviceName = String(hit.object.userData.securityTitle || 'Security device');
      const deviceKind = String(hit.object.userData.securityKind || 'camera');
      elements.selectionKind.textContent = deviceKind === 'turret'
        ? 'Auto Turret'
        : (deviceKind === 'drone' ? 'Drone camera' : 'Security camera');
      elements.selectionName.textContent = deviceName;
      elements.selectionDetail.textContent = hit.object.userData.securityDetail || '';
      elements.selectionDetail.hidden = !elements.selectionDetail.textContent;
      elements.selectionTeamButton.hidden = true;
      elements.selectionTeamButton.dataset.memberId = '';
      elements.selectionTrailButton.hidden = true;
      elements.selectionTrailButton.dataset.memberId = '';
      elements.selectionSecurityViewButton.dataset.deviceId = deviceId;
      elements.selectionSecurityViewButton.textContent = deviceKind === 'turret' ? 'View turret camera' : 'View camera';
      elements.selectionSecurityViewButton.hidden = !deviceId || !runtime.securityFrameAvailable;
      elements.selectionPanel.hidden = false;
      return;
    }
    if (hit?.object?.userData?.teamIntelTitle) {
      const memberId = String(hit.object.userData.teamIntelMemberId || '');
      const memberName = String(hit.object.userData.teamIntelTitle || 'Teammate');
      elements.selectionKind.textContent = hit.object.userData.teamIntelKind || 'Team Intel';
      elements.selectionName.textContent = memberName;
      elements.selectionDetail.textContent = hit.object.userData.teamIntelDetail || '';
      elements.selectionDetail.hidden = !elements.selectionDetail.textContent;
      elements.selectionTeamButton.dataset.memberId = memberId;
      elements.selectionTeamButton.textContent = `Show only ${memberName.slice(0, 28)} live marker`;
      elements.selectionTeamButton.hidden = !memberId || typeof runtime.focusTeamMember !== 'function';
      elements.selectionTrailButton.dataset.memberId = memberId;
      elements.selectionTrailButton.textContent = `Show only ${memberName.slice(0, 28)} trail`;
      elements.selectionTrailButton.hidden = !memberId || typeof runtime.focusTrailMember !== 'function';
      elements.selectionSecurityViewButton.hidden = true;
      elements.selectionSecurityViewButton.dataset.deviceId = '';
      if (memberId && typeof runtime.highlightTrailMember === 'function') {
        runtime.highlightTrailMember(memberId);
      }
      elements.selectionPanel.hidden = false;
      return;
    }
    if (!hit?.object?.userData?.monument) {
      elements.selectionPanel.hidden = true;
      elements.selectionDetail.hidden = true;
      elements.selectionTeamButton.hidden = true;
      elements.selectionTeamButton.dataset.memberId = '';
      elements.selectionTrailButton.hidden = true;
      elements.selectionTrailButton.dataset.memberId = '';
      elements.selectionSecurityViewButton.hidden = true;
      elements.selectionSecurityViewButton.dataset.deviceId = '';
      return;
    }
    elements.selectionKind.textContent = 'Selected monument';
    elements.selectionName.textContent = hit.object.userData.monument;
    elements.selectionDetail.hidden = true;
    elements.selectionTeamButton.hidden = true;
    elements.selectionTeamButton.dataset.memberId = '';
    elements.selectionTrailButton.hidden = true;
    elements.selectionTrailButton.dataset.memberId = '';
    elements.selectionSecurityViewButton.hidden = true;
    elements.selectionSecurityViewButton.dataset.deviceId = '';
    elements.selectionPanel.hidden = false;
  };
  elements.canvas.addEventListener('pointerdown', onPointerDown);
  elements.canvas.addEventListener('pointerup', onPointerUp);
  runtime.addCleanup(() => elements.canvas.removeEventListener('pointerdown', onPointerDown));
  runtime.addCleanup(() => elements.canvas.removeEventListener('pointerup', onPointerUp));
  runtime.addCleanup(() => {
    elements.mapControls.hidden = true;
    elements.overlay.hidden = true;
    elements.overlayInfo.hidden = true;
    elements.monumentFilterPanel.hidden = true;
    elements.teamFilterPanel.hidden = true;
    elements.teamFilterButton.hidden = true;
    elements.teamFilterButton.setAttribute('aria-expanded', 'false');
    elements.deathFilterPanel.hidden = true;
    elements.deathFilterButton.hidden = true;
    elements.deathFilterButton.setAttribute('aria-expanded', 'false');
    elements.trailFilterPanel.hidden = true;
    elements.trailFilterButton.hidden = true;
    elements.trailFilterButton.setAttribute('aria-expanded', 'false');
    elements.trailRangePicker.hidden = true;
    elements.trails.hidden = true;
    elements.cameras.hidden = true;
    elements.turrets.hidden = true;
    elements.securityFacing.hidden = true;
    runtime.closeSecurityViewer?.();
    elements.selectionPanel.hidden = true;
    elements.selectionDetail.hidden = true;
    elements.selectionTrailButton.hidden = true;
    elements.selectionTrailButton.dataset.memberId = '';
    elements.selectionSecurityViewButton.hidden = true;
    elements.selectionSecurityViewButton.dataset.deviceId = '';
    elements.team.hidden = true;
    elements.deaths.hidden = true;
    elements.teamIntelPanel.hidden = true;
  });
}


function securityDeviceKindLabel(kind) {
  if (kind === 'turret') return 'Auto Turret';
  if (kind === 'drone') return 'Drone camera';
  return 'Rust+ camera';
}

function securityDeviceGlyph(kind) {
  if (kind === 'turret') return '⌖';
  if (kind === 'drone') return '◇';
  return '▣';
}

function securityDeviceAge(lastSeen) {
  const timestamp = Number(lastSeen || 0);
  if (!Number.isFinite(timestamp) || timestamp <= 0) return 'position pending';
  const seconds = Math.max(0, Math.floor(Date.now() / 1000) - timestamp);
  if (seconds < 10) return 'seen now';
  if (seconds < 60) return `seen ${seconds}s ago`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `seen ${minutes}m ago`;
  return `seen ${Math.floor(minutes / 60)}h ago`;
}

async function loadCamsDashboard(metadata, _config, _instanceId) {
  const devicesUrl = String(metadata.security_devices_url || metadata.team_intel_url || '');
  const frameBase = String(metadata.security_frame_base_url || '');
  if (!devicesUrl || !frameBase) {
    const error = new Error('MEG did not provide an authorized Rust+ camera session.');
    error.code = 'camera_dashboard_unavailable';
    throw error;
  }

  elements.objectName.textContent = metadata.object_name || 'MEG Security Cameras';
  elements.canvas.hidden = true;
  elements.canvas.classList.remove('ready');
  if (elements.toolbar) elements.toolbar.hidden = true;
  elements.mapControls.hidden = true;
  elements.selectionPanel.hidden = true;
  elements.teamIntelPanel.hidden = true;
  elements.securityViewerPanel.hidden = true;
  elements.camsDashboard.hidden = false;
  elements.loading.classList.add('hidden');
  setConnected('Cameras · read-only');

  let stopped = false;
  let paused = false;
  let deviceTimer = null;
  let frameTimer = null;
  let frameVersion = 0;
  let activeDevice = null;
  let currentDevices = [];
  let frameBlobUrl = '';
  let deviceRequestInFlight = false;
  let frameRequestInFlight = false;
  let frameAbort = null;
  let frameEtag = '';
  let frameUnchangedCount = 0;
  let lastFrameAt = 0;
  let localSampleCount = 0;

  const clearTimer = (name) => {
    if (name === 'device' && deviceTimer !== null) {
      clearTimeout(deviceTimer);
      deviceTimer = null;
    }
    if (name === 'frame' && frameTimer !== null) {
      clearTimeout(frameTimer);
      frameTimer = null;
    }
  };

  const replaceFrameBlob = (blob) => {
    const nextUrl = URL.createObjectURL(blob);
    const previous = frameBlobUrl;
    frameBlobUrl = nextUrl;
    elements.camsLiveImage.src = nextUrl;
    elements.camsLiveImage.hidden = false;
    elements.camsLiveLoading.textContent = '';
    if (previous) URL.revokeObjectURL(previous);
  };

  const scheduleDevices = (delay = 5000) => {
    clearTimer('device');
    if (!stopped && !paused) deviceTimer = setTimeout(() => void refreshDevices(false), delay);
  };

  const scheduleFrame = (version, delay = 1100) => {
    clearTimer('frame');
    if (!stopped && !paused && activeDevice && version === frameVersion) {
      frameTimer = setTimeout(() => void refreshFrame(version), delay);
    }
  };

  const closeFeed = ({ keepSelection = false } = {}) => {
    frameVersion += 1;
    clearTimer('frame');
    frameAbort?.abort();
    frameAbort = null;
    frameRequestInFlight = false;
    frameEtag = '';
    frameUnchangedCount = 0;
    if (!keepSelection) {
      activeDevice = null;
      localSampleCount = 0;
    }
    elements.camsLiveImage.hidden = true;
    elements.camsLiveImage.removeAttribute('src');
    if (frameBlobUrl) {
      URL.revokeObjectURL(frameBlobUrl);
      frameBlobUrl = '';
    }
    if (!keepSelection) {
      elements.camsActiveKind.textContent = 'Rust+ camera';
      elements.camsActiveName.textContent = 'Choose a camera';
      elements.camsLiveLoading.textContent = 'Choose a camera or turret from the list.';
      elements.camsLiveStatus.textContent = 'Read-only · detail improves as Rust+ camera samples accumulate';
      elements.camsStop.hidden = true;
    }
    renderDeviceList();
  };

  const selectDevice = (device) => {
    if (!device?.id) return;
    if (activeDevice?.id === device.id) return;
    closeFeed({ keepSelection: false });
    activeDevice = { ...device };
    frameVersion += 1;
    const version = frameVersion;
    const kind = String(device.kind || 'camera');
    elements.camsActiveKind.textContent = securityDeviceKindLabel(kind);
    elements.camsActiveName.textContent = String(device.name || 'Security device').slice(0, 80);
    const hintedWidth = Math.max(0, Number(device.width || 0));
    const hintedHeight = Math.max(0, Number(device.height || 0));
    elements.camsQualityPill.textContent = hintedWidth && hintedHeight
      ? `Sampling · ${hintedWidth}×${hintedHeight} native`
      : 'Sampling Rust+…';
    elements.camsStop.hidden = false;
    elements.camsLiveImage.hidden = true;
    elements.camsLiveLoading.textContent = device.view_available === false
      ? (device.view_reason === 'paired_player_online'
        ? 'Camera viewing is paused while the paired Rust account is connected to the server.'
        : 'This Rust+ camera is unavailable right now.')
      : 'Opening camera · collecting native Rust+ samples…';
    elements.camsLiveStatus.textContent = device.view_available === false
      ? 'Last-known device remains registered · live camera unavailable'
      : 'Read-only · waiting for native Rust+ camera samples';
    renderDeviceList();
    if (device.view_available !== false && !paused) void refreshFrame(version);
  };

  function renderDeviceList() {
    elements.camsDeviceList.replaceChildren();
    if (!currentDevices.length) {
      const empty = document.createElement('div');
      empty.className = 'cams-empty-state';
      empty.textContent = 'No security cameras or turrets are registered yet. Use /security add or !addcam in Rust team chat.';
      elements.camsDeviceList.appendChild(empty);
      return;
    }
    for (const device of currentDevices) {
      const kind = String(device.kind || 'camera');
      const button = document.createElement('button');
      button.type = 'button';
      button.className = `cams-device-button cams-device-${kind}`;
      button.classList.toggle('selected', Boolean(activeDevice?.id && activeDevice.id === device.id));
      button.dataset.deviceId = String(device.id || '');

      const icon = document.createElement('span');
      icon.className = 'cams-device-icon';
      icon.textContent = securityDeviceGlyph(kind);
      const copy = document.createElement('span');
      copy.className = 'cams-device-copy';
      const name = document.createElement('strong');
      name.textContent = String(device.name || 'Security device').slice(0, 80);
      const meta = document.createElement('small');
      const availability = device.view_available === false
        ? (device.view_reason === 'paired_player_online' ? 'view paused' : 'unavailable')
        : 'live available';
      meta.textContent = `${securityDeviceKindLabel(kind)} · ${availability} · ${securityDeviceAge(device.last_seen)}`;
      copy.append(name, meta);
      button.append(icon, copy);
      button.addEventListener('click', () => selectDevice(device));
      elements.camsDeviceList.appendChild(button);
    }
  }

  async function refreshDevices(userRequested = false) {
    if (stopped || paused || deviceRequestInFlight) return;
    deviceRequestInFlight = true;
    if (userRequested) elements.camsRefresh.disabled = true;
    try {
      const payload = await requestJson(`${devicesUrl}?t=${Date.now()}`, { cache: 'no-store' });
      if (stopped) return;
      if (!payload?.available) {
        elements.camsDeviceSummary.textContent = payload?.message || 'Rust+ security data is unavailable right now.';
        currentDevices = [];
        renderDeviceList();
        return;
      }
      currentDevices = Array.isArray(payload.security_devices)
        ? payload.security_devices.filter((item) => item && item.id)
        : [];
      const cameraCount = currentDevices.filter((item) => String(item.kind || 'camera') === 'camera').length;
      const turretCount = currentDevices.filter((item) => String(item.kind || '') === 'turret').length;
      const droneCount = currentDevices.filter((item) => String(item.kind || '') === 'drone').length;
      const countParts = [];
      if (cameraCount) countParts.push(`${cameraCount} camera${cameraCount === 1 ? '' : 's'}`);
      if (turretCount) countParts.push(`${turretCount} turret${turretCount === 1 ? '' : 's'}`);
      if (droneCount) countParts.push(`${droneCount} drone${droneCount === 1 ? '' : 's'}`);
      const pairedOnline = Boolean(payload?.security?.paired_player_online);
      elements.camsDeviceSummary.textContent = countParts.length
        ? `${countParts.join(' · ')}${pairedOnline ? ' · live view paused while paired Rust account is in-game' : ''}`
        : 'No registered cameras or turrets yet.';

      if (activeDevice) {
        const refreshed = currentDevices.find((item) => item.id === activeDevice.id);
        if (refreshed) activeDevice = { ...refreshed };
        else closeFeed();
      }
      renderDeviceList();

      if (!activeDevice && currentDevices.length) {
        const preferred = currentDevices.find((item) => item.view_available !== false) || currentDevices[0];
        selectDevice(preferred);
      }
    } catch (error) {
      if (stopped || activityClosed) return;
      elements.camsDeviceSummary.textContent = 'MEG could not refresh the security-device list.';
      console.warn('Cameras dashboard device refresh failed', redactedDiagnostic(error));
    } finally {
      deviceRequestInFlight = false;
      elements.camsRefresh.disabled = false;
      scheduleDevices();
    }
  }

  async function refreshFrame(version) {
    const device = activeDevice;
    if (stopped || paused || frameRequestInFlight || !device || version !== frameVersion) return;
    frameRequestInFlight = true;
    const controller = new AbortController();
    frameAbort = controller;
    const frameUrl = `${frameBase}/${encodeURIComponent(device.id)}/frame?viewer=${encodeURIComponent(cameraViewerId)}`;
    try {
      const response = await fetch(apiUrl(frameUrl), {
        cache: 'no-cache',
        headers: cameraRequestHeaders(frameEtag),
        signal: controller.signal,
      });
      const responseEtag = String(response.headers.get('etag') || '').trim();
      if (responseEtag) frameEtag = responseEtag;
      if (response.status === 304) {
        frameUnchangedCount += 1;
        if (version === frameVersion && !stopped && activeDevice) {
          scheduleFrame(version, cameraRefreshDelay({
            unchangedCount: frameUnchangedCount,
            ready: true,
            hidden: document.hidden,
          }));
        }
        return;
      }
      const contentType = String(response.headers.get('content-type') || '').toLowerCase();
      const sourceSize = String(response.headers.get('x-meg-camera-source') || '').trim();
      const displaySize = String(response.headers.get('x-meg-camera-display') || '').trim();
      const enhancement = String(response.headers.get('x-meg-camera-enhancement') || '').trim();
      const packetCount = Math.max(0, Number.parseInt(response.headers.get('x-meg-camera-packets') || '0', 10) || 0);
      const readyAt = Math.max(1, Number.parseInt(response.headers.get('x-meg-camera-ready-at') || '10', 10) || 10);
      const cameraReady = response.headers.get('x-meg-camera-ready') === '1';
      const cachedFrame = response.headers.get('x-meg-camera-cached') === '1';
      if (!response.ok || contentType.includes('application/json')) {
        let payload = {};
        try { payload = await response.json(); } catch (_) { payload = {}; }
        const reason = String(payload?.reason || payload?.error || 'camera_unavailable');
        const message = String(payload?.message || 'That Rust+ camera is unavailable right now.');
        if (version !== frameVersion || stopped) return;
        elements.camsLiveImage.hidden = true;
        elements.camsLiveLoading.textContent = message;
        elements.camsQualityPill.textContent = 'Camera unavailable';
        elements.camsLiveStatus.textContent = reason === 'paired_player_online'
          ? 'Live view resumes after the paired Rust account disconnects from the server'
          : `Read-only · ${reason.replaceAll('_', ' ')}`;
        scheduleFrame(version, 2200);
        return;
      }
      const blob = await response.blob();
      if (version !== frameVersion || stopped || !activeDevice) return;
      frameUnchangedCount = 0;
      replaceFrameBlob(blob);
      lastFrameAt = Date.now();
      // Custom response headers should survive the Activity proxy, but keep a
      // local fallback so the quality indicator still progresses if a proxy
      // strips them. Server packet counts remain authoritative when present.
      localSampleCount = packetCount > 0 ? packetCount : Math.min(readyAt, localSampleCount + 1);
      const effectiveReady = cameraReady || localSampleCount >= readyAt;
      const progress = Math.min(localSampleCount, readyAt);
      const sourceText = sourceSize && sourceSize !== 'unknown' ? `${sourceSize} Rust+ source` : 'Rust+ source';
      const displayText = displaySize && displaySize !== 'unknown' ? `${displaySize} display` : 'smoothed display';
      const smoothMode = enhancement === 'smooth_hd';
      elements.camsQualityPill.textContent = effectiveReady
        ? `${smoothMode ? 'Smooth HD' : 'Ready'} · ${sourceSize && sourceSize !== 'unknown' ? `${sourceSize} source` : 'Rust+ source'}`
        : `Sampling ${progress}/${readyAt}`;
      const cacheText = cachedFrame ? ' · waiting for next sample' : '';
      elements.camsLiveStatus.textContent = `${displayText} from ${sourceText} · ${new Date(lastFrameAt).toLocaleTimeString()}${cacheText} · read-only`;
      scheduleFrame(version, cameraRefreshDelay({ ready: effectiveReady, hidden: document.hidden }));
    } catch (error) {
      if (isCameraAbort(error)) return;
      if (activityClosed || stopped || version !== frameVersion) return;
      elements.camsLiveLoading.textContent = elements.camsLiveImage.hidden
        ? 'MEG could not refresh that camera yet.'
        : '';
      elements.camsLiveStatus.textContent = 'Read-only · retrying live Rust+ camera…';
      console.warn('Cameras dashboard frame refresh failed', redactedDiagnostic(error));
      scheduleFrame(version, cameraRefreshDelay({ failed: true, hidden: document.hidden }));
    } finally {
      if (frameAbort === controller) {
        frameAbort = null;
        frameRequestInFlight = false;
      }
    }
  }

  const runtime = {
    controls: { autoRotate: false },
    invalidate() {},
    isStopped: () => stopped,
    resetView() {},
    pause() {
      if (stopped || paused) return;
      paused = true;
      clearTimer('device');
      clearTimer('frame');
      frameAbort?.abort();
      frameAbort = null;
      frameRequestInFlight = false;
      activityLifecycle.rendererPaused = true;
    },
    resume() {
      if (stopped || !paused) return;
      paused = false;
      activityLifecycle.rendererPaused = false;
      void refreshDevices(false);
      if (activeDevice) scheduleFrame(frameVersion, 60);
    },
    stop() {
      if (stopped) return;
      stopped = true;
      paused = true;
      clearTimer('device');
      clearTimer('frame');
      closeFeed();
      elements.camsDashboard.hidden = true;
      activityLifecycle.rendererStopped = true;
      activityLifecycle.rendererPaused = true;
    },
  };
  viewerRuntime = runtime;
  activityLifecycle.rendererStopped = false;
  activityLifecycle.rendererPaused = false;
  activityLifecycle.phase = 'interactive';

  elements.camsRefresh.onclick = () => void refreshDevices(true);
  elements.camsStop.onclick = () => closeFeed();
  await refreshDevices(true);
}

async function loadWorld(metadata, config, instanceId) {
  elements.objectName.textContent = metadata.object_name;
  document.title = `${metadata.object_name} · MEG 3D Viewer`;
  setLoading('Loading real Rust terrain…', 'Reading the verified map package', 16);
  const manifest = await requestJson(metadata.world_url);
  const overview = manifest.mode === 'map_overview';
  const reliefScale = Number(
    overview
      ? manifest.display?.overview_relief_scale ?? 0.32
      : manifest.display?.detail_relief_scale ?? 1,
  );
  if (!(reliefScale > 0 && reliefScale <= 2)) {
    throw new Error('The map presentation scale is invalid.');
  }
  const chunkMeta = manifest.terrain?.chunks?.[0];
  const focusAsset = manifest.assets?.find((asset) => asset.role === 'focus_monument');
  if (!chunkMeta?.url) {
    throw new Error('The verified map package is incomplete.');
  }

  if (viewerRuntime) viewerRuntime.stop();
  const runtime = createViewer();
  viewerRuntime = runtime;
  runtime.renderer.shadowMap.enabled = false;
  runtime.renderer.toneMappingExposure = 0.78;
  for (const light of runtime.scene.children.filter((child) => child.isLight)) {
    if (light.isHemisphereLight) light.intensity = 0.58;
    else if (light === runtime.key) light.intensity = 1.25;
    else light.intensity = 0.24;
  }
  runtime.scene.background = new THREE.Color(0x82aac2);

  setLoading('Loading real Rust terrain…', 'Downloading decoded map heights and surface', 28);
  const terrainBufferPromise = fetchBinary(chunkMeta.url);
  const surfaceTexturePromise = loadSurfaceTexture(runtime, manifest.terrain?.texture?.url)
    .catch((error) => {
      console.warn('Terrain texture unavailable; using vertex colours', redactedDiagnostic(error));
      return null;
    });
  const [terrainBuffer, surfaceTexture] = await Promise.all([
    terrainBufferPromise,
    surfaceTexturePromise,
  ]);
  const terrainChunk = decodeTerrainChunk(terrainBuffer, chunkMeta);
  const terrainMesh = buildTerrainMesh(terrainChunk, manifest, reliefScale, surfaceTexture);
  runtime.scene.add(terrainMesh);

  const bounds = new THREE.Box3().setFromObject(terrainMesh);
  const terrainSize = bounds.getSize(new THREE.Vector3());
  const span = Math.max(terrainSize.x, terrainSize.z, 200);
  runtime.scene.fog = new THREE.FogExp2(0x8db0c2, Math.min(0.00065, 0.46 / span));
  const waterMaterial = new THREE.MeshPhysicalMaterial({
    color: 0x23677d,
    roughness: 0.38,
    metalness: 0,
    transparent: false,
    depthWrite: true,
  });
  const water = buildOceanSurface(terrainChunk, manifest, reliefScale, waterMaterial);
  runtime.scene.add(water);

  const layerGroups = {
    roads: new THREE.Group(),
    railways: new THREE.Group(),
    rivers: new THREE.Group(),
    icons: new THREE.Group(),
    labels: new THREE.Group(),
    models: new THREE.Group(),
    team: new THREE.Group(),
    deaths: new THREE.Group(),
    trails: new THREE.Group(),
    cameras: new THREE.Group(),
    turrets: new THREE.Group(),
    securityFacing: new THREE.Group(),
    overlay: new THREE.Group(),
    grid: new THREE.Group(),
  };
  for (const [name, group] of Object.entries(layerGroups)) {
    group.name = `map-${name}`;
    runtime.scene.add(group);
  }
  const defaults = manifest.layer_defaults || {};
  layerGroups.roads.visible = defaults.roads_trails !== false;
  layerGroups.railways.visible = defaults.railways !== false;
  layerGroups.rivers.visible = defaults.rivers !== false;
  layerGroups.icons.visible = defaults.monument_icons !== false;
  layerGroups.labels.visible = defaults.monument_labels !== false;
  layerGroups.models.visible = defaults.monument_models !== false;
  layerGroups.team.visible = true;
  layerGroups.deaths.visible = true;
  layerGroups.trails.visible = false;
  layerGroups.cameras.visible = true;
  layerGroups.turrets.visible = true;
  layerGroups.securityFacing.visible = true;
  layerGroups.overlay.visible = defaults.overlay !== false;
  layerGroups.grid.visible = defaults.grid === true;
  const teamIntelEnabled = Boolean(metadata.team_intel_url);
  const teamTrailsEnabled = teamIntelEnabled && Boolean(metadata.team_trails_url);
  elements.team.hidden = !teamIntelEnabled;
  elements.teamFilterButton.hidden = true;
  elements.teamFilterPanel.hidden = true;
  elements.teamFilterButton.setAttribute('aria-expanded', 'false');
  elements.deaths.hidden = !teamIntelEnabled;
  elements.deathFilterButton.hidden = true;
  elements.deathFilterPanel.hidden = true;
  elements.deathFilterButton.setAttribute('aria-expanded', 'false');
  elements.trails.hidden = !teamTrailsEnabled;
  elements.trailFilterButton.hidden = true;
  elements.trailFilterPanel.hidden = true;
  elements.trailFilterButton.setAttribute('aria-expanded', 'false');
  elements.trailRangePicker.hidden = !teamTrailsEnabled;
  elements.cameras.hidden = true;
  elements.turrets.hidden = true;
  elements.securityFacing.hidden = true;
  elements.teamIntelPanel.hidden = !teamIntelEnabled;
  const overlayControlsAvailable = await configureOverlaySelector(
    runtime,
    manifest,
    layerGroups.overlay,
    terrainMesh,
  );
  const pathCount = addWorldPaths(runtime, manifest, terrainChunk, reliefScale, layerGroups);
  const markerCount = addWorldMarkers(runtime, manifest, terrainChunk, span, reliefScale, layerGroups);
  const gridCells = addWorldGrid(manifest, terrainChunk, reliefScale, layerGroups.grid);
  const monumentFilters = configureMonumentFilters(runtime, manifest, layerGroups);

  if (!(manifest.placements || []).length && focusAsset?.url && manifest.focus?.scene_position) {
    try {
      setLoading(`Loading ${manifest.focus.name}…`, 'Downloading its verified RustRelay GLB', 52);
      const gltf = await loadGltf(focusAsset.url, (event) => {
        if (!event.lengthComputable || event.total <= 0) return;
        setLoading(
          `Loading ${manifest.focus.name}…`,
          `${Math.round((event.loaded / event.total) * 100)}% of the verified monument GLB`,
          52 + Math.min(38, (event.loaded / event.total) * 38),
        );
      });
      if (activityClosed) {
        disposeSceneResources(gltf.scene);
        return;
      }
      const root = gltf.scene;
      root.position.fromArray(manifest.focus.scene_position);
      root.position.y *= reliefScale;
      root.quaternion.fromArray(manifest.focus.scene_quaternion);
      root.scale.fromArray(manifest.focus.scale);
      root.traverse((object) => {
        if (object.isLight) object.visible = false;
        if (!object.isMesh) return;
        object.castShadow = false;
        object.receiveShadow = false;
        const materials = Array.isArray(object.material) ? object.material : [object.material];
        for (const material of materials) {
          if (!material) continue;
          material.envMapIntensity = 0.42;
          for (const key of ['map', 'normalMap', 'roughnessMap', 'metalnessMap', 'aoMap']) {
            const texture = material[key];
            if (texture) texture.anisotropy = Math.min(4, runtime.renderer.capabilities.getMaxAnisotropy());
          }
        }
      });
      runtime.scene.add(root);
      root.updateMatrixWorld(true);
    } catch (error) {
      if (activityClosed) return;
      console.warn('Optional monument GLB was skipped', redactedDiagnostic(error));
    }
  }

  const focus = overview
    ? new THREE.Vector3(0, 0, 0)
    : new THREE.Vector3().fromArray(manifest.focus?.scene_position || [0, 0, 0]);
  focus.y *= reliefScale;
  runtime.camera.fov = 42;
  runtime.camera.near = 0.5;
  runtime.camera.far = Math.max(4000, span * 8);
  runtime.camera.position.copy(focus).add(
    overview
      ? new THREE.Vector3(0, span * 0.19, span * 0.82)
      : new THREE.Vector3(span * 0.34, span * 0.24, span * 0.38),
  );
  runtime.camera.updateProjectionMatrix();
  runtime.controls.target.copy(focus).add(new THREE.Vector3(0, overview ? 5 : 22, 0));
  runtime.controls.minDistance = overview ? 35 : 20;
  runtime.controls.maxDistance = span * 3;
  runtime.controls.update();
  runtime.controls.saveState();
  configureMapControls(
    runtime,
    [
      [elements.icons, layerGroups.icons, 'Icons'],
      [elements.labels, layerGroups.labels, 'Names'],
      [elements.roads, layerGroups.roads, 'Roads'],
      [elements.rail, layerGroups.railways, 'Rail'],
      [elements.rivers, layerGroups.rivers, 'Rivers'],
      [elements.models, layerGroups.models, 'Models'],
      ...(teamIntelEnabled
        ? [
          [elements.team, layerGroups.team, 'Team'],
          [elements.deaths, layerGroups.deaths, 'Deaths'],
          ...(teamTrailsEnabled ? [[elements.trails, layerGroups.trails, 'Trails']] : []),
          [elements.cameras, layerGroups.cameras, 'Cameras'],
          [elements.turrets, layerGroups.turrets, 'Turrets'],
          [elements.securityFacing, layerGroups.securityFacing, 'Facing'],
        ]
        : []),
      ...(overlayControlsAvailable
        ? [[elements.overlay, layerGroups.overlay, 'Overlay']]
        : []),
      [elements.grid, layerGroups.grid, 'Grid'],
    ],
    span,
    runtime.controls.target.clone(),
    terrainMesh,
    manifest,
    reliefScale,
  );
  if (teamIntelEnabled) {
    configureTeamIntel(
      runtime,
      metadata,
      manifest,
      terrainChunk,
      span,
      reliefScale,
      layerGroups.team,
      layerGroups.deaths,
      layerGroups.trails,
      layerGroups.cameras,
      layerGroups.turrets,
      layerGroups.securityFacing,
    );
  }
  runtime.invalidate(5);

  elements.reset.disabled = false;
  elements.rotate.disabled = false;
  elements.canvas.classList.add('ready');
  elements.loading.classList.add('hidden');
  setConnected(overview ? 'Interactive full map' : 'Interactive map slice');
  activityLifecycle.phase = 'interactive';
  let loadedModelInstances = 0;
  streamWorldModels(
    runtime,
    manifest,
    reliefScale,
    layerGroups.models,
    monumentFilters,
    (instances, _activeAssets, totalAssets, modelsVisible, desiredInstances) => {
      loadedModelInstances = instances;
      if (!modelsVisible) {
        setConnected('Interactive · models off');
      } else if (instances && instances < desiredInstances) {
        setConnected(`Interactive · loading ${instances}/${desiredInstances} visible models`);
      } else if (instances) {
        setConnected(`Interactive · ${instances} visible models`);
      } else {
        setConnected(totalAssets ? 'Interactive · move closer to stream models' : 'Interactive · terrain only');
      }
    },
  );
  if (debugRequested && config.dev_mode) {
    const metrics = {
      mode: manifest.mode,
      monument: manifest.focus?.name || 'none',
      terrain: `${chunkMeta.samples} × ${chunkMeta.samples}`,
      span: `${terrainSize.x.toFixed(0)} × ${terrainSize.z.toFixed(0)} m`,
      paths: pathCount,
      markers: markerCount,
      requestedModels: (manifest.placements || []).length,
      get loadedModels() { return loadedModelInstances; },
      gridCells,
      relief: reliefScale,
      texture: surfaceTexture
        ? `${manifest.terrain.texture.width} × ${manifest.terrain.texture.height}`
        : 'vertex fallback',
      instance: instanceId,
    };
    showDiagnostics(metrics);
    window.__MEG_3D_DEV_STATE__ = () => ({
      metrics,
      camera: runtime.camera.position.toArray(),
      target: runtime.controls.target.toArray(),
      autoRotate: runtime.controls.autoRotate,
      pixelRatio: runtime.renderer.getPixelRatio(),
      performance: runtime.performanceMetrics(),
      visibleMonuments: layerGroups.models.visible ? layerGroups.models.children.length : 0,
      loadedTextures: runtime.renderer.info.memory.textures,
      cursor: runtime.cursorWorld || null,
    });
  }
}

async function loadModel(metadata, config, instanceId) {
  elements.mapControls.hidden = true;
  elements.team.hidden = true;
  elements.deaths.hidden = true;
  elements.deathFilterButton.hidden = true;
  elements.deathFilterPanel.hidden = true;
  elements.deathFilterButton.setAttribute('aria-expanded', 'false');
  elements.trails.hidden = true;
  elements.trailFilterButton.hidden = true;
  elements.trailFilterPanel.hidden = true;
  elements.trailFilterButton.setAttribute('aria-expanded', 'false');
  elements.trailRangePicker.hidden = true;
  elements.cameras.hidden = true;
  elements.turrets.hidden = true;
  elements.securityFacing.hidden = true;
  elements.selectionTrailButton.hidden = true;
  elements.selectionTrailButton.dataset.memberId = '';
  elements.teamIntelPanel.hidden = true;
  elements.monumentFilterButton.hidden = true;
  elements.monumentFilterPanel.hidden = true;
  elements.overlayPicker.hidden = true;
  elements.overlay.hidden = true;
  elements.overlayInfo.hidden = true;
  elements.selectionPanel.hidden = true;
  elements.selectionDetail.hidden = true;
  elements.objectName.textContent = metadata.object_name;
  document.title = `${metadata.object_name} · MEG 3D Viewer`;
  setLoading(`Loading ${metadata.object_name}…`, 'Downloading the verified Rust model', 18);

  if (viewerRuntime) viewerRuntime.stop();
  const runtime = createViewer();
  viewerRuntime = runtime;
  const draco = new DRACOLoader();
  draco.setDecoderPath(apiUrl(__MEG_DRACO_DECODER_PATH__));
  draco.setDecoderConfig({ type: 'wasm' });
  draco.preload();
  const loader = new GLTFLoader();
  loader.setDRACOLoader(draco);
  loader.setMeshoptDecoder(MeshoptDecoder);

  const started = performance.now();
  let gltf;
  try {
    gltf = await new Promise((resolve, reject) => {
      loader.load(
        apiUrl(metadata.model_url),
        resolve,
        (event) => {
          if (event.lengthComputable && event.total > 0) {
            const percent = 18 + Math.min(68, (event.loaded / event.total) * 68);
            setLoading(
              `Loading ${metadata.object_name}…`,
              `${Math.round((event.loaded / event.total) * 100)}% of the verified GLB`,
              percent,
            );
          } else {
            setLoading(`Loading ${metadata.object_name}…`, 'Receiving the verified GLB', 35);
          }
        },
        reject,
      );
    });
  } finally {
    draco.dispose();
  }

  if (activityClosed) {
    disposeSceneResources(gltf.scene);
    return;
  }

  const root = gltf.scene;
  root.traverse((object) => {
    if (!object.isMesh) return;
    object.castShadow = true;
    object.receiveShadow = true;
    const materials = Array.isArray(object.material) ? object.material : [object.material];
    for (const material of materials) {
      if (!material) continue;
      material.side = THREE.DoubleSide;
      material.envMapIntensity = 0.55;
      for (const key of ['map', 'normalMap', 'roughnessMap', 'metalnessMap', 'aoMap']) {
        const texture = material[key];
        if (texture) {
          texture.anisotropy = Math.min(8, runtime.renderer.capabilities.getMaxAnisotropy());
          texture.needsUpdate = true;
        }
      }
      material.needsUpdate = true;
    }
  });
  runtime.scene.add(root);
  const bounds = frameModel(runtime, root);
  runtime.renderer.shadowMap.needsUpdate = true;
  runtime.invalidate(4);
  const loadMs = performance.now() - started;

  elements.reset.disabled = false;
  elements.rotate.disabled = false;
  elements.canvas.classList.add('ready');
  elements.loading.classList.add('hidden');
  setConnected('Interactive');
  activityLifecycle.phase = 'interactive';

  if (debugRequested && config.dev_mode) {
    const metrics = {
      ...sceneMetrics(root, gltf, metadata, bounds, loadMs, runtime),
      instance: instanceId,
    };
    showDiagnostics(metrics);
    window.__MEG_3D_DEV_STATE__ = () => ({
      metrics,
      camera: runtime.camera.position.toArray(),
      target: runtime.controls.target.toArray(),
      autoRotate: runtime.controls.autoRotate,
      pixelRatio: runtime.renderer.getPixelRatio(),
      performance: runtime.performanceMetrics(),
    });
  }
}

async function start() {
  elements.canvas.hidden = false;
  if (elements.toolbar) elements.toolbar.hidden = false;
  if (elements.camsDashboard) elements.camsDashboard.hidden = true;
  elements.mapControls.hidden = true;
  elements.team.hidden = true;
  elements.deaths.hidden = true;
  elements.deathFilterButton.hidden = true;
  elements.deathFilterPanel.hidden = true;
  elements.deathFilterButton.setAttribute('aria-expanded', 'false');
  elements.trails.hidden = true;
  elements.trailFilterButton.hidden = true;
  elements.trailFilterPanel.hidden = true;
  elements.trailFilterButton.setAttribute('aria-expanded', 'false');
  elements.trailRangePicker.hidden = true;
  elements.selectionTrailButton.hidden = true;
  elements.selectionTrailButton.dataset.memberId = '';
  elements.teamIntelPanel.hidden = true;
  elements.participantsPanel.hidden = true;
  elements.overlay.hidden = true;
  elements.selectionPanel.hidden = true;
  elements.selectionDetail.hidden = true;
  elements.error.hidden = true;
  elements.canvas.classList.remove('ready');
  elements.reset.disabled = true;
  elements.rotate.disabled = true;
  setLoading('Opening MEG Activity…', 'Verifying this Discord Activity session', 1);
  try {
    const { config, session, instanceId } = await openActivitySession();
    if (session.mode === 'megunity') {
      if (session.activity_path !== '/megunity/') {
        throw new Error('Meg Unity returned an invalid Activity route.');
      }
      sessionStorage.setItem('meg_unity_activity_auth_v1', JSON.stringify({
        version: 1, instanceId, accessToken: discordAuthentication.accessToken,
        userId: discordAuthentication.userId, expiresAt: discordAuthentication.expiresAt,
      }));
      activityLifecycle.phase = 'routing-megunity';
      window.location.replace(session.activity_path);
      return;
    }
    if (session.mode === 'neko') {
      const path = typeof session.activity_path === 'string' ? session.activity_path : '';
      if (!path.startsWith('/') || path.startsWith('//')) {
        throw new Error('MEG Neko returned an invalid Activity route.');
      }
      storeNekoActivityAuthentication(instanceId);
      activityLifecycle.phase = 'routing-neko';
      window.location.replace(path);
      return;
    }
    void startParticipantTracking();
    try {
      if (session.mode === 'cams' || session.viewer_kind === 'cameras') {
        await loadCamsDashboard(session, config, instanceId);
      } else if (session.viewer_kind === 'map') {
        await loadWorld(session, config, instanceId);
      } else {
        await loadModel(session, config, instanceId);
      }
    } catch (error) {
      console.error('Verified 3D content load failed', redactedDiagnostic(error));
      const safe = new Error(
        'The verified Rust 3D view could not be loaded by this device. Close the viewer and try again.',
      );
      safe.code = 'viewer_load_failed';
      throw safe;
    }
  } catch (error) {
    if (activityClosed) return;
    if (viewerRuntime) {
      viewerRuntime.stop();
      viewerRuntime = null;
    }
    activityLifecycle.phase = 'error';
    console.error('MEG 3D viewer failed', redactedDiagnostic(error));
    const expired = ['session_expired', 'model_expired', 'invalid_selection'].includes(error.code);
    showError(
      expired ? 'Activity session expired' : 'Couldn’t open this Activity',
      error.message || 'Run /watchneko or use a fresh MEG Activity button and try again.',
      expired ? null : start,
    );
  }
}

elements.reset.addEventListener('click', () => {
  if (!viewerRuntime) return;
  if (viewerRuntime.resetView) {
    viewerRuntime.resetView();
    return;
  }
  const damping = viewerRuntime.controls.enableDamping;
  const autoRotate = viewerRuntime.controls.autoRotate;
  viewerRuntime.controls.enableDamping = false;
  viewerRuntime.controls.autoRotate = false;
  // The second reset clears any residual damped orbit delta left by a drag.
  viewerRuntime.controls.reset();
  viewerRuntime.controls.reset();
  viewerRuntime.controls.autoRotate = autoRotate;
  viewerRuntime.controls.enableDamping = damping;
  viewerRuntime.controls.update();
});

elements.rotate.addEventListener('click', () => {
  if (!viewerRuntime) return;
  viewerRuntime.controls.autoRotate = !viewerRuntime.controls.autoRotate;
  viewerRuntime.invalidate(4);
  elements.rotate.setAttribute('aria-pressed', String(viewerRuntime.controls.autoRotate));
  elements.rotate.lastChild.textContent = viewerRuntime.controls.autoRotate
    ? ' Auto Rotate: On'
    : ' Auto Rotate: Off';
});

elements.retry.addEventListener('click', () => retryAction?.());
elements.canvas.addEventListener('webglcontextlost', (event) => {
  event.preventDefault();
  if (activityClosed) return;
  showError(
    '3D rendering paused',
    'Discord lost access to the graphics context. Close and reopen this viewer.',
  );
});

document.addEventListener('visibilitychange', () => {
  if (!viewerRuntime || activityClosed) return;
  if (document.hidden) {
    viewerRuntime.pause();
    activityLifecycle.phase = 'hidden';
  } else {
    viewerRuntime.resume();
    viewerRuntime.resumeSecurityViewer?.();
    activityLifecycle.phase = 'interactive';
  }
});

function closeActivity(reason) {
  if (activityClosed) return;
  activityClosed = true;
  activityLifecycle.phase = 'closed';
  activityLifecycle.closeReason = reason;
  activityAbort.abort();
  if (viewerRuntime) {
    viewerRuntime.stop({ releaseContext: true });
    viewerRuntime = null;
  }
  stopParticipantTracking();
  if (discordSdk) {
    try {
      discordSdk.close(RPCCloseCodes.CLOSE_NORMAL, '');
    } catch {
      // Discord may already have removed the iframe or closed its RPC source.
    }
  }
  discordSdk = null;
  discordReady = null;
  discordAuthentication = null;
}

window.addEventListener('pagehide', () => closeActivity('pagehide'), { once: true });
window.addEventListener('beforeunload', () => closeActivity('beforeunload'), { once: true });

start();
